import { ThemeTokens } from '@/types/theme';

// ── Shared helpers ──────────────────────────────────────────────────────
const defaultTypo = {
  fontGlobal: "'Inter', sans-serif",
  fontSizeH1: '2.25rem',
  fontSizeH2: '1.875rem',
  fontSizeH3: '1.5rem',
  fontSizeP: '1rem',
  sidebarGridIconSize: '1.5rem',
  sidebarGridTextSize: '0.75rem',
};
const defaultSpacing = {
  headerHeight: '50px',
  playerHeight: '80px',
  sidebarWidth: '16rem',
  sidebarWidthIcon: '50px',
  filterBarHeight: '50px',
  scrollTopOffset: '120px',
};
const lightShadows = {
  shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  shadowMd: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  shadowLg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  shadowXl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
};
const darkShadows = {
  shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.3)',
  shadowMd: '0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
  shadowLg: '0 10px 15px -3px rgb(0 0 0 / 0.3), 0 4px 6px -4px rgb(0 0 0 / 0.3)',
  shadowXl: '0 20px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3)',
};

// =====================================================================
//  1. LIGHT  –  pure white background, pure black text, zero colour
// =====================================================================
export const plainLightTokens: ThemeTokens = {
  colors: {
    background: '0 0% 100%',        // pure white
    foreground: '0 0% 0%',          // pure black
    headerBackground: '0 0% 100%',
    card: '0 0% 100%',
    cardForeground: '0 0% 0%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 0%',
    primary: '0 0% 0%',             // pure black buttons
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 0% 8%',
    secondary: '0 0% 93%',
    secondaryForeground: '0 0% 0%',
    muted: '0 0% 93%',
    mutedForeground: '0 0% 32%',    // darker gray — more readable
    accent: '0 0% 10%',
    accentForeground: '0 0% 100%',
    destructive: '0 72% 51%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 80%',             // clearly visible borders
    input: '0 0% 85%',
    ring: '0 0% 0%',
    sidebarBackground: '0 0% 0%',   // pure black sidebar
    sidebarForeground: '0 0% 100%',
    sidebarPrimary: '0 0% 100%',
    sidebarPrimaryForeground: '0 0% 0%',
    sidebarAccent: '0 0% 15%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '0 0% 20%',
    sidebarRing: '0 0% 40%',
    chart1: '0 0% 0%',
    chart2: '0 0% 25%',
    chart3: '0 0% 50%',
    chart4: '0 0% 68%',
    chart5: '0 0% 85%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: lightShadows,
};

// =====================================================================
//  2. DARK  –  neutral graphite background, pure white text, zero colour
// =====================================================================
export const plainDarkTokens: ThemeTokens = {
  colors: {
    background: '0 0% 9%',          // graphite #171717
    foreground: '0 0% 100%',        // pure white
    headerBackground: '0 0% 11%',
    card: '0 0% 13%',
    cardForeground: '0 0% 100%',
    popover: '0 0% 13%',
    popoverForeground: '0 0% 100%',
    primary: '0 0% 100%',           // pure white buttons
    primaryForeground: '0 0% 0%',
    playerToggleBackground: '0 0% 100%',
    secondary: '0 0% 17%',
    secondaryForeground: '0 0% 100%',
    muted: '0 0% 17%',
    mutedForeground: '0 0% 65%',    // lighter gray — more readable on graphite
    accent: '0 0% 90%',
    accentForeground: '0 0% 0%',
    destructive: '0 72% 51%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 22%',             // clearly visible on graphite
    input: '0 0% 18%',
    ring: '0 0% 100%',
    sidebarBackground: '0 0% 9%',   // graphite sidebar
    sidebarForeground: '0 0% 100%',
    sidebarPrimary: '0 0% 100%',
    sidebarPrimaryForeground: '0 0% 0%',
    sidebarAccent: '0 0% 17%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '0 0% 16%',
    sidebarRing: '0 0% 60%',
    chart1: '0 0% 100%',
    chart2: '0 0% 78%',
    chart3: '0 0% 55%',
    chart4: '0 0% 35%',
    chart5: '0 0% 18%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

// =====================================================================
//  3. HIGH CONTRAST / VISUALLY IMPAIRED
//  Research consensus: black-on-white is best for low vision
//  (white-on-black suits photosensitivity but is less common overall)
//  Sources: WCAG 1.4.3, RNIB, Vision Australia guidelines
// =====================================================================
const accessibleTypo = {
  fontGlobal: "'Inter', sans-serif",
  fontSizeH1: '3rem',         // 48px — very large headings
  fontSizeH2: '2.5rem',       // 40px
  fontSizeH3: '2rem',         // 32px
  fontSizeP: '1.3125rem',     // 21px — clearly readable body text
  sidebarGridIconSize: '1.75rem',
  sidebarGridTextSize: '0.9375rem',
};

export const accessibleLightTokens: ThemeTokens = {
  colors: {
    background: '0 0% 100%',   // pure white — maximum contrast base
    foreground: '0 0% 0%',     // pure black — 21:1 contrast ratio
    headerBackground: '0 0% 100%',
    card: '0 0% 100%',
    cardForeground: '0 0% 0%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 0%',
    primary: '0 0% 0%',
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 0% 8%',
    secondary: '0 0% 90%',
    secondaryForeground: '0 0% 0%',
    muted: '0 0% 90%',
    mutedForeground: '0 0% 20%',  // dark gray — still very readable
    accent: '0 0% 0%',
    accentForeground: '0 0% 100%',
    destructive: '0 85% 38%',  // deep red — clearly visible
    destructiveForeground: '0 0% 100%',
    border: '0 0% 50%',        // very visible borders
    input: '0 0% 80%',
    ring: '0 0% 0%',
    sidebarBackground: '0 0% 0%',
    sidebarForeground: '0 0% 100%',
    sidebarPrimary: '0 0% 100%',
    sidebarPrimaryForeground: '0 0% 0%',
    sidebarAccent: '0 0% 18%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '0 0% 35%',
    sidebarRing: '0 0% 60%',
    chart1: '0 0% 0%',
    chart2: '0 0% 20%',
    chart3: '0 0% 40%',
    chart4: '0 0% 60%',
    chart5: '0 0% 80%',
  },
  typography: accessibleTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: lightShadows,
};

// =====================================================================
//  3b. HIGH CONTRAST DARK — white-on-black variant (for photosensitivity)
// =====================================================================
export const accessibleDarkTokens: ThemeTokens = {
  colors: {
    background: '0 0% 0%',    // pure black
    foreground: '0 0% 100%',  // pure white — 21:1 contrast ratio
    headerBackground: '0 0% 5%',
    card: '0 0% 8%',
    cardForeground: '0 0% 100%',
    popover: '0 0% 8%',
    popoverForeground: '0 0% 100%',
    primary: '0 0% 100%',
    primaryForeground: '0 0% 0%',
    playerToggleBackground: '0 0% 100%',
    secondary: '0 0% 14%',
    secondaryForeground: '0 0% 100%',
    muted: '0 0% 14%',
    mutedForeground: '0 0% 78%',
    accent: '0 0% 90%',
    accentForeground: '0 0% 0%',
    destructive: '0 80% 65%',  // bright red — visible on black
    destructiveForeground: '0 0% 0%',
    border: '0 0% 50%',
    input: '0 0% 20%',
    ring: '0 0% 100%',
    sidebarBackground: '0 0% 0%',
    sidebarForeground: '0 0% 100%',
    sidebarPrimary: '0 0% 100%',
    sidebarPrimaryForeground: '0 0% 0%',
    sidebarAccent: '0 0% 16%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '0 0% 35%',
    sidebarRing: '0 0% 60%',
    chart1: '0 0% 100%',
    chart2: '0 0% 78%',
    chart3: '0 0% 55%',
    chart4: '0 0% 35%',
    chart5: '0 0% 18%',
  },
  typography: accessibleTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  4. BB LIGHT  –  white background, purple accents
// =====================================================================
// =====================================================================
//  LOW VISION  –  yellow & white on black, extra-large text (Gordon 2026-11-02:
//  "anpassa app för synskadade och gör ett tema som är anpassat för synnedsatta").
//  Yellow on black is the pairing many people with low vision find easiest.
//  Every accessibility theme also turns on html[data-a11y] (globals.css):
//  solid surfaces, no text under 14px, thick focus rings, no motion.
// =====================================================================
const lowVisionTypo = {
  fontGlobal: "'Atkinson Hyperlegible', sans-serif", // no 'Inter' here — loadFont skips any stack that names Inter
  fontSizeH1: '3.25rem',
  fontSizeH2: '2.75rem',
  fontSizeH3: '2.25rem',
  fontSizeP: '1.375rem',      // 22px body text
  sidebarGridIconSize: '2rem',
  sidebarGridTextSize: '1rem',
};

export const lowVisionTokens: ThemeTokens = {
  colors: {
    background: '0 0% 0%',
    foreground: '0 0% 100%',         // white on black — 21:1
    headerBackground: '0 0% 0%',
    card: '0 0% 6%',
    cardForeground: '0 0% 100%',
    popover: '0 0% 6%',
    popoverForeground: '0 0% 100%',
    primary: '54 100% 50%',          // signal yellow — about 19:1 on black
    primaryForeground: '0 0% 0%',
    playerToggleBackground: '54 100% 50%',
    secondary: '0 0% 16%',
    secondaryForeground: '0 0% 100%',
    muted: '0 0% 16%',
    mutedForeground: '0 0% 88%',     // secondary text stays bright
    accent: '54 100% 50%',
    accentForeground: '0 0% 0%',
    destructive: '0 90% 70%',
    destructiveForeground: '0 0% 0%',
    border: '0 0% 75%',              // every edge clearly visible
    input: '0 0% 12%',
    ring: '54 100% 50%',             // yellow focus ring
    sidebarBackground: '0 0% 0%',
    sidebarForeground: '0 0% 100%',
    sidebarPrimary: '54 100% 50%',
    sidebarPrimaryForeground: '0 0% 0%',
    sidebarAccent: '0 0% 18%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '0 0% 60%',
    sidebarRing: '54 100% 50%',
    chart1: '54 100% 50%',
    chart2: '0 0% 100%',
    chart3: '190 100% 60%',
    chart4: '0 0% 65%',
    chart5: '30 100% 60%',
  },
  typography: lowVisionTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.25rem' },
  shadows: darkShadows,
};

export const bbLightTokens: ThemeTokens = {
  colors: {
    background: '240 5% 96%',
    foreground: '240 10% 3.9%',
    headerBackground: '0 0% 100%',
    card: '0 0% 100%',
    cardForeground: '240 10% 3.9%',
    popover: '0 0% 100%',
    popoverForeground: '240 10% 3.9%',
    primary: '266 86% 65%',
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '136 86% 55%',
    secondary: '240 4.8% 95.9%',
    secondaryForeground: '240 5.9% 10%',
    muted: '240 4.8% 95.9%',
    mutedForeground: '240 3.8% 46.1%',
    accent: '192 63% 60%',
    accentForeground: '240 5.9% 10%',
    destructive: '0 84.2% 60.2%',
    destructiveForeground: '0 0% 98%',
    border: '240 5.9% 90%',
    input: '240 5.9% 90%',
    ring: '266 86% 65%',
    sidebarBackground: '240 10% 15%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '266 86% 65%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '240 10% 25%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '240 10% 25%',
    sidebarRing: '266 86% 60%',
    chart1: '266 86% 65%',
    chart2: '192 63% 60%',
    chart3: '35 91% 65%',
    chart4: '10 89% 65%',
    chart5: '240 5% 65%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: lightShadows,
};

// =====================================================================
//  4. BB DARK  –  dark background, purple accents
// =====================================================================
export const bbDarkTokens: ThemeTokens = {
  colors: {
    background: '240 10% 10%',
    foreground: '0 0% 95%',
    headerBackground: '240 10% 15%',
    card: '240 10% 18%',
    cardForeground: '0 0% 95%',
    popover: '240 10% 15%',
    popoverForeground: '0 0% 95%',
    primary: '266 86% 70%',
    primaryForeground: '0 0% 5%',
    playerToggleBackground: '136 86% 55%',
    secondary: '240 10% 25%',
    secondaryForeground: '0 0% 90%',
    muted: '240 10% 25%',
    mutedForeground: '0 0% 70%',
    accent: '192 63% 68%',
    accentForeground: '0 0% 5%',
    destructive: '0 62.8% 30.6%',
    destructiveForeground: '0 0% 98%',
    border: '240 10% 25%',
    input: '240 10% 18%',
    ring: '266 86% 70%',
    sidebarBackground: '240 10% 8%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '266 86% 65%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '240 10% 20%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '240 10% 20%',
    sidebarRing: '266 86% 60%',
    chart1: '266 86% 70%',
    chart2: '192 63% 68%',
    chart3: '35 91% 70%',
    chart4: '10 89% 70%',
    chart5: '240 5% 70%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

// =====================================================================
//  5. AFRICAN  –  red, gold & green  (Pan-African colours)
// =====================================================================
export const africanTokens: ThemeTokens = {
  colors: {
    background: '40 30% 10%',          // deep warm brown-black
    foreground: '45 90% 85%',          // warm gold text
    headerBackground: '40 30% 13%',
    card: '40 25% 15%',
    cardForeground: '45 80% 85%',
    popover: '40 25% 15%',
    popoverForeground: '45 80% 85%',
    primary: '0 75% 50%',              // rich red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '45 95% 55%', // gold
    secondary: '40 20% 20%',
    secondaryForeground: '45 80% 85%',
    muted: '40 15% 20%',
    mutedForeground: '40 20% 55%',
    accent: '140 65% 40%',             // green
    accentForeground: '0 0% 100%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '40 20% 22%',
    input: '40 20% 18%',
    ring: '45 95% 55%',                // gold ring
    sidebarBackground: '40 30% 8%',
    sidebarForeground: '45 80% 80%',
    sidebarPrimary: '0 75% 50%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '40 20% 18%',
    sidebarAccentForeground: '45 80% 90%',
    sidebarBorder: '40 20% 18%',
    sidebarRing: '45 95% 55%',
    chart1: '0 75% 50%',               // red
    chart2: '45 95% 55%',              // gold
    chart3: '140 65% 40%',             // green
    chart4: '25 85% 55%',              // orange
    chart5: '40 30% 45%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Nunito', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

// =====================================================================
//  6. JAMAICAN  –  black, yellow & green  (Jamaican flag)
// =====================================================================
export const jamaicanTokens: ThemeTokens = {
  colors: {
    background: '0 0% 8%',             // black (Jamaica flag)
    foreground: '52 100% 80%',         // warm yellow text
    headerBackground: '52 100% 50%',   // gold header bar
    card: '140 28% 13%',               // green-tinted card (no neutral gray)
    cardForeground: '52 90% 82%',
    popover: '140 28% 13%',
    popoverForeground: '52 90% 82%',
    primary: '52 100% 50%',            // vibrant yellow / gold
    primaryForeground: '0 0% 5%',
    playerToggleBackground: '140 70% 42%', // green
    secondary: '140 22% 17%',          // dark green-tinted (was neutral gray)
    secondaryForeground: '52 90% 80%',
    muted: '140 18% 15%',              // dark green-tinted muted
    mutedForeground: '52 40% 58%',     // gold-tinted muted text
    accent: '140 70% 42%',             // green
    accentForeground: '0 0% 100%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '140 20% 20%',             // green-tinted border
    input: '140 18% 14%',
    ring: '52 100% 50%',
    sidebarBackground: '0 0% 5%',      // near-black sidebar
    sidebarForeground: '52 80% 75%',
    sidebarPrimary: '52 100% 50%',
    sidebarPrimaryForeground: '0 0% 5%',
    sidebarAccent: '140 25% 14%',
    sidebarAccentForeground: '52 80% 85%',
    sidebarBorder: '140 18% 16%',
    sidebarRing: '52 100% 50%',
    chart1: '52 100% 50%',             // yellow
    chart2: '140 70% 42%',             // green
    chart3: '52 60% 65%',
    chart4: '140 50% 55%',
    chart5: '52 80% 40%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Poppins', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  7. FRENCH  –  blue, white & red  (Le Tricolore)
// =====================================================================
export const frenchTokens: ThemeTokens = {
  colors: {
    background: '0 0% 97%',            // near-white
    foreground: '220 60% 18%',         // dark navy text
    headerBackground: '220 55% 22%',   // navy header
    card: '0 0% 100%',
    cardForeground: '220 55% 18%',
    popover: '0 0% 100%',
    popoverForeground: '220 55% 18%',
    primary: '220 75% 48%',            // French blue
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 75% 50%', // red
    secondary: '220 20% 93%',
    secondaryForeground: '220 55% 18%',
    muted: '220 15% 93%',
    mutedForeground: '220 10% 50%',
    accent: '0 75% 50%',               // red accent
    accentForeground: '0 0% 100%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '220 15% 88%',
    input: '220 15% 90%',
    ring: '220 75% 48%',
    sidebarBackground: '220 55% 18%',  // navy sidebar
    sidebarForeground: '0 0% 95%',
    sidebarPrimary: '0 0% 100%',
    sidebarPrimaryForeground: '220 55% 18%',
    sidebarAccent: '220 50% 28%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '220 45% 28%',
    sidebarRing: '0 75% 50%',
    chart1: '220 75% 48%',             // blue
    chart2: '0 75% 50%',               // red
    chart3: '0 0% 60%',                // grey
    chart4: '200 60% 55%',             // light blue
    chart5: '350 60% 60%',             // rose
  },
  typography: { ...defaultTypo, fontGlobal: "'Playfair Display', serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: lightShadows,
};

// =====================================================================
//  8. JAPANESE  –  white & red  (Hinomaru)
// =====================================================================
export const japaneseTokens: ThemeTokens = {
  colors: {
    background: '0 0% 97%',            // soft white
    foreground: '0 0% 12%',            // near-black text
    headerBackground: '0 0% 100%',
    card: '0 0% 100%',
    cardForeground: '0 0% 12%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 12%',
    primary: '0 80% 48%',              // Hinomaru red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 80% 48%',
    secondary: '0 10% 94%',
    secondaryForeground: '0 0% 15%',
    muted: '0 8% 94%',
    mutedForeground: '0 0% 48%',
    accent: '0 65% 55%',               // lighter red accent
    accentForeground: '0 0% 100%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '0 5% 89%',
    input: '0 5% 91%',
    ring: '0 80% 48%',
    sidebarBackground: '0 0% 10%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '0 80% 55%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '0 0% 18%',
    sidebarAccentForeground: '0 0% 95%',
    sidebarBorder: '0 0% 18%',
    sidebarRing: '0 80% 48%',
    chart1: '0 80% 48%',               // red
    chart2: '0 0% 25%',                // charcoal
    chart3: '0 40% 65%',               // rose
    chart4: '0 0% 70%',                // grey
    chart5: '15 50% 55%',              // warm rust
  },
  typography: { ...defaultTypo, fontGlobal: "'Noto Sans JP', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: lightShadows,
};

// =====================================================================
//  9. SWEDISH  –  yellow & blue  (Blågult)
// =====================================================================
export const swedishTokens: ThemeTokens = {
  colors: {
    background: '216 65% 15%',         // Swedish dark blue
    foreground: '48 95% 85%',          // golden text
    headerBackground: '216 65% 20%',
    card: '216 55% 22%',
    cardForeground: '48 90% 85%',
    popover: '216 55% 22%',
    popoverForeground: '48 90% 85%',
    primary: '48 100% 52%',            // Swedish yellow
    primaryForeground: '216 65% 15%',
    playerToggleBackground: '48 100% 52%',
    secondary: '216 45% 25%',
    secondaryForeground: '48 80% 85%',
    muted: '216 40% 25%',
    mutedForeground: '216 20% 55%',
    accent: '48 85% 60%',              // softer gold
    accentForeground: '216 65% 12%',
    destructive: '0 75% 55%',
    destructiveForeground: '0 0% 100%',
    border: '216 40% 27%',
    input: '216 45% 22%',
    ring: '48 100% 52%',
    sidebarBackground: '216 65% 12%',
    sidebarForeground: '48 70% 80%',
    sidebarPrimary: '48 100% 52%',
    sidebarPrimaryForeground: '216 65% 12%',
    sidebarAccent: '216 50% 22%',
    sidebarAccentForeground: '48 80% 90%',
    sidebarBorder: '216 45% 22%',
    sidebarRing: '48 100% 52%',
    chart1: '48 100% 52%',             // yellow
    chart2: '216 65% 50%',             // blue
    chart3: '190 50% 55%',             // teal
    chart4: '40 80% 60%',              // amber
    chart5: '216 30% 45%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Inter', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  10. RETRO TERMINAL  –  black background, phosphor green
// =====================================================================
export const retroTerminalTokens: ThemeTokens = {
  colors: {
    background: '120 35% 7%',          // phosphor dark green
    foreground: '120 100% 50%',        // full green text
    headerBackground: '120 10% 7%',
    card: '120 10% 8%',
    cardForeground: '120 100% 50%',
    popover: '120 10% 8%',
    popoverForeground: '120 100% 50%',
    primary: '120 100% 45%',           // bright green
    primaryForeground: '120 10% 4%',   // dark bg on green buttons
    playerToggleBackground: '120 100% 45%',
    secondary: '120 10% 12%',
    secondaryForeground: '120 80% 50%',
    muted: '120 8% 12%',
    mutedForeground: '120 40% 35%',    // dim green
    accent: '120 100% 40%',
    accentForeground: '120 10% 4%',
    destructive: '0 80% 45%',
    destructiveForeground: '120 100% 50%',
    border: '120 30% 15%',
    input: '120 10% 10%',
    ring: '120 100% 45%',
    sidebarBackground: '120 10% 3%',
    sidebarForeground: '120 80% 45%',
    sidebarPrimary: '120 100% 45%',
    sidebarPrimaryForeground: '120 10% 4%',
    sidebarAccent: '120 15% 12%',
    sidebarAccentForeground: '120 100% 50%',
    sidebarBorder: '120 20% 12%',
    sidebarRing: '120 100% 45%',
    chart1: '120 100% 50%',
    chart2: '120 70% 40%',
    chart3: '120 50% 30%',
    chart4: '120 100% 35%',
    chart5: '120 30% 20%',
  },
  typography: { ...defaultTypo, fontGlobal: "'JetBrains Mono', 'Courier New', monospace" },
  spacing: defaultSpacing,
  radius: { radius: '0px' },           // sharp corners like a terminal
  shadows: darkShadows,
};

// =====================================================================
//  11. CHINESE  –  red & yellow / gold  (中国红)
// =====================================================================
export const chineseTokens: ThemeTokens = {
  colors: {
    background: '0 60% 12%',           // very dark red-black
    foreground: '45 95% 82%',          // golden text
    headerBackground: '0 55% 16%',
    card: '0 45% 18%',
    cardForeground: '45 90% 82%',
    popover: '0 45% 18%',
    popoverForeground: '45 90% 82%',
    primary: '0 80% 50%',              // Chinese red
    primaryForeground: '45 100% 90%',  // gold text on red
    playerToggleBackground: '45 95% 55%', // gold
    secondary: '0 35% 22%',
    secondaryForeground: '45 80% 80%',
    muted: '0 25% 20%',
    mutedForeground: '0 15% 50%',
    accent: '45 95% 55%',              // gold accent
    accentForeground: '0 60% 12%',
    destructive: '0 90% 55%',
    destructiveForeground: '0 0% 100%',
    border: '0 30% 24%',
    input: '0 35% 18%',
    ring: '45 95% 55%',
    sidebarBackground: '0 55% 10%',
    sidebarForeground: '45 80% 78%',
    sidebarPrimary: '0 80% 50%',
    sidebarPrimaryForeground: '45 100% 90%',
    sidebarAccent: '0 40% 18%',
    sidebarAccentForeground: '45 85% 85%',
    sidebarBorder: '0 35% 18%',
    sidebarRing: '45 95% 55%',
    chart1: '0 80% 50%',               // red
    chart2: '45 95% 55%',              // gold
    chart3: '20 80% 52%',              // vermilion
    chart4: '35 80% 50%',              // amber
    chart5: '0 30% 40%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Noto Sans SC', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  MUSIC GENRE THEMES
// =====================================================================

export const hipHopTokens: ThemeTokens = {
  colors: {
    background: '38 28% 9%',            // warm amber-black
    foreground: '0 0% 96%',
    headerBackground: '38 25% 12%',
    card: '38 22% 13%',
    cardForeground: '0 0% 96%',
    popover: '0 0% 10%',
    popoverForeground: '0 0% 96%',
    primary: '43 100% 52%',            // gold
    primaryForeground: '0 0% 5%',
    playerToggleBackground: '43 100% 52%',
    secondary: '38 18% 16%',
    secondaryForeground: '45 80% 80%',
    muted: '38 16% 15%',
    mutedForeground: '38 12% 55%',
    accent: '35 95% 55%',              // amber
    accentForeground: '0 0% 5%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '38 15% 18%',
    input: '38 16% 14%',
    ring: '43 100% 52%',
    sidebarBackground: '38 32% 6%',
    sidebarForeground: '45 70% 80%',
    sidebarPrimary: '43 100% 52%',
    sidebarPrimaryForeground: '0 0% 5%',
    sidebarAccent: '38 22% 12%',
    sidebarAccentForeground: '45 80% 85%',
    sidebarBorder: '0 0% 14%',
    sidebarRing: '43 100% 52%',
    chart1: '43 100% 52%',             // gold
    chart2: '35 90% 50%',              // amber
    chart3: '0 0% 55%',                // grey
    chart4: '0 80% 52%',               // red
    chart5: '0 0% 30%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Rajdhani', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: darkShadows,
};

export const jazzTokens: ThemeTokens = {
  colors: {
    background: '228 55% 14%',         // rich midnight navy
    foreground: '45 70% 88%',          // warm cream
    headerBackground: '228 50% 17%',
    card: '228 48% 18%',
    cardForeground: '45 60% 88%',
    popover: '228 30% 15%',
    popoverForeground: '45 60% 88%',
    primary: '45 85% 55%',             // warm gold
    primaryForeground: '228 35% 10%',
    playerToggleBackground: '45 85% 55%',
    secondary: '228 25% 20%',
    secondaryForeground: '45 60% 82%',
    muted: '228 20% 20%',
    mutedForeground: '228 15% 55%',
    accent: '35 65% 48%',              // amber-gold
    accentForeground: '228 35% 10%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '228 20% 22%',
    input: '228 25% 18%',
    ring: '45 85% 55%',
    sidebarBackground: '228 40% 7%',
    sidebarForeground: '45 60% 80%',
    sidebarPrimary: '45 85% 55%',
    sidebarPrimaryForeground: '228 35% 8%',
    sidebarAccent: '228 25% 16%',
    sidebarAccentForeground: '45 70% 85%',
    sidebarBorder: '228 20% 16%',
    sidebarRing: '45 75% 50%',
    chart1: '45 85% 55%',
    chart2: '35 65% 48%',
    chart3: '228 50% 60%',
    chart4: '20 60% 50%',
    chart5: '228 25% 45%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Playfair Display', serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

export const classicalTokens: ThemeTokens = {
  colors: {
    background: '40 35% 96%',          // warm ivory
    foreground: '30 15% 12%',
    headerBackground: '40 30% 100%',
    card: '40 25% 100%',
    cardForeground: '30 15% 12%',
    popover: '40 25% 100%',
    popoverForeground: '30 15% 12%',
    primary: '350 55% 35%',            // deep burgundy
    primaryForeground: '40 60% 95%',
    playerToggleBackground: '45 65% 45%',
    secondary: '40 20% 92%',
    secondaryForeground: '30 15% 15%',
    muted: '40 15% 92%',
    mutedForeground: '30 10% 48%',
    accent: '45 65% 45%',              // antique gold
    accentForeground: '40 60% 95%',
    destructive: '0 72% 45%',
    destructiveForeground: '0 0% 100%',
    border: '40 15% 85%',
    input: '40 20% 88%',
    ring: '350 55% 35%',
    sidebarBackground: '30 20% 15%',
    sidebarForeground: '40 40% 85%',
    sidebarPrimary: '45 65% 55%',
    sidebarPrimaryForeground: '30 20% 10%',
    sidebarAccent: '30 15% 22%',
    sidebarAccentForeground: '40 35% 88%',
    sidebarBorder: '30 15% 22%',
    sidebarRing: '45 65% 50%',
    chart1: '350 55% 35%',
    chart2: '45 65% 45%',
    chart3: '228 35% 50%',
    chart4: '30 40% 45%',
    chart5: '40 20% 65%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Cormorant Garamond', 'Georgia', serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: lightShadows,
};

export const electronicTokens: ThemeTokens = {
  colors: {
    background: '228 55% 9%',          // deep electric navy
    foreground: '0 0% 95%',
    headerBackground: '228 50% 12%',
    card: '228 48% 13%',
    cardForeground: '0 0% 95%',
    popover: '240 18% 9%',
    popoverForeground: '0 0% 95%',
    primary: '186 100% 50%',           // neon cyan
    primaryForeground: '240 20% 5%',
    playerToggleBackground: '280 90% 65%',
    secondary: '228 40% 15%',
    secondaryForeground: '186 60% 80%',
    muted: '240 12% 14%',
    mutedForeground: '240 10% 55%',
    accent: '280 90% 65%',             // electric purple
    accentForeground: '0 0% 100%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '240 15% 18%',
    input: '240 18% 12%',
    ring: '186 100% 50%',
    sidebarBackground: '240 22% 3%',
    sidebarForeground: '186 40% 75%',
    sidebarPrimary: '186 100% 50%',
    sidebarPrimaryForeground: '240 20% 5%',
    sidebarAccent: '228 42% 12%',
    sidebarAccentForeground: '186 60% 85%',
    sidebarBorder: '240 15% 14%',
    sidebarRing: '186 80% 50%',
    chart1: '186 100% 50%',
    chart2: '280 90% 65%',
    chart3: '200 100% 55%',
    chart4: '260 80% 70%',
    chart5: '160 90% 45%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Rajdhani', 'Inter', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: darkShadows,
};

export const rockTokens: ThemeTokens = {
  colors: {
    background: '352 38% 8%',          // dark blood-red
    foreground: '0 0% 90%',
    headerBackground: '352 32% 11%',
    card: '352 30% 12%',
    cardForeground: '0 0% 90%',
    popover: '352 30% 12%',
    popoverForeground: '0 0% 90%',
    primary: '0 80% 52%',              // rock red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 80% 52%',
    secondary: '352 22% 16%',
    secondaryForeground: '0 0% 85%',
    muted: '352 18% 15%',
    mutedForeground: '352 10% 50%',
    accent: '0 0% 40%',               // steel grey
    accentForeground: '0 0% 100%',
    destructive: '0 85% 55%',
    destructiveForeground: '0 0% 100%',
    border: '352 20% 18%',
    input: '352 18% 13%',
    ring: '0 80% 52%',
    sidebarBackground: '352 42% 5%',
    sidebarForeground: '0 0% 80%',
    sidebarPrimary: '0 80% 52%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '352 25% 12%',
    sidebarAccentForeground: '0 0% 90%',
    sidebarBorder: '352 18% 14%',
    sidebarRing: '0 75% 48%',
    chart1: '0 80% 52%',
    chart2: '0 0% 60%',
    chart3: '0 0% 40%',
    chart4: '0 60% 45%',
    chart5: '0 0% 25%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Oswald', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.05rem' },
  shadows: darkShadows,
};

export const rnbTokens: ThemeTokens = {
  colors: {
    background: '275 52% 12%',         // rich plum
    foreground: '295 15% 92%',
    headerBackground: '275 47% 15%',
    card: '275 44% 16%',
    cardForeground: '295 12% 92%',
    popover: '275 30% 12%',
    popoverForeground: '295 12% 92%',
    primary: '315 70% 60%',            // rose-purple
    primaryForeground: '275 35% 8%',
    playerToggleBackground: '290 60% 65%',
    secondary: '275 25% 17%',
    secondaryForeground: '295 12% 85%',
    muted: '275 20% 16%',
    mutedForeground: '275 15% 55%',
    accent: '285 55% 65%',             // soft purple
    accentForeground: '275 35% 8%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '275 22% 20%',
    input: '275 25% 15%',
    ring: '315 70% 60%',
    sidebarBackground: '275 40% 5%',
    sidebarForeground: '295 12% 82%',
    sidebarPrimary: '315 70% 60%',
    sidebarPrimaryForeground: '275 35% 6%',
    sidebarAccent: '275 25% 13%',
    sidebarAccentForeground: '295 12% 88%',
    sidebarBorder: '275 20% 14%',
    sidebarRing: '315 60% 58%',
    chart1: '315 70% 60%',
    chart2: '285 55% 65%',
    chart3: '260 60% 65%',
    chart4: '340 60% 58%',
    chart5: '275 30% 45%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Nunito', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

export const bluesTokens: ThemeTokens = {
  colors: {
    background: '220 55% 12%',         // rich steel blue
    foreground: '210 20% 90%',
    headerBackground: '220 50% 15%',
    card: '220 48% 16%',
    cardForeground: '210 18% 90%',
    popover: '220 28% 12%',
    popoverForeground: '210 18% 90%',
    primary: '210 80% 55%',            // bright blue
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '200 70% 48%',
    secondary: '220 22% 18%',
    secondaryForeground: '210 15% 83%',
    muted: '220 18% 17%',
    mutedForeground: '220 12% 52%',
    accent: '200 60% 45%',             // steel blue
    accentForeground: '0 0% 100%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '220 20% 20%',
    input: '220 22% 15%',
    ring: '210 80% 55%',
    sidebarBackground: '220 35% 5%',
    sidebarForeground: '210 18% 80%',
    sidebarPrimary: '210 80% 55%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '220 22% 13%',
    sidebarAccentForeground: '210 15% 85%',
    sidebarBorder: '220 18% 14%',
    sidebarRing: '210 70% 50%',
    chart1: '210 80% 55%',
    chart2: '200 60% 45%',
    chart3: '230 60% 60%',
    chart4: '190 50% 50%',
    chart5: '220 25% 45%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Merriweather', serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

export const countryTokens: ThemeTokens = {
  colors: {
    background: '35 40% 96%',          // warm natural light
    foreground: '30 30% 12%',
    headerBackground: '35 35% 100%',
    card: '35 30% 100%',
    cardForeground: '30 28% 12%',
    popover: '35 30% 100%',
    popoverForeground: '30 28% 12%',
    primary: '25 75% 42%',             // leather brown
    primaryForeground: '35 60% 96%',
    playerToggleBackground: '45 85% 50%',
    secondary: '35 25% 91%',
    secondaryForeground: '30 25% 15%',
    muted: '35 20% 91%',
    mutedForeground: '30 15% 48%',
    accent: '45 85% 50%',              // hay gold
    accentForeground: '30 30% 10%',
    destructive: '0 72% 45%',
    destructiveForeground: '0 0% 100%',
    border: '35 18% 85%',
    input: '35 22% 88%',
    ring: '25 75% 42%',
    sidebarBackground: '30 35% 14%',
    sidebarForeground: '35 35% 85%',
    sidebarPrimary: '45 85% 55%',
    sidebarPrimaryForeground: '30 35% 10%',
    sidebarAccent: '30 25% 22%',
    sidebarAccentForeground: '35 30% 88%',
    sidebarBorder: '30 22% 22%',
    sidebarRing: '45 75% 48%',
    chart1: '25 75% 42%',
    chart2: '45 85% 50%',
    chart3: '15 60% 45%',
    chart4: '35 50% 50%',
    chart5: '35 20% 65%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Merriweather', serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: lightShadows,
};

// =====================================================================
//  DESIGN STYLE THEMES
// =====================================================================

export const bauhausTokens: ThemeTokens = {
  colors: {
    background: '0 0% 100%',           // pure white
    foreground: '0 0% 5%',
    headerBackground: '0 0% 100%',
    card: '0 0% 98%',
    cardForeground: '0 0% 5%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 5%',
    primary: '0 90% 50%',              // Bauhaus red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '50 100% 48%',
    secondary: '0 0% 95%',
    secondaryForeground: '0 0% 8%',
    muted: '0 0% 93%',
    mutedForeground: '0 0% 40%',
    accent: '210 100% 48%',            // Bauhaus blue
    accentForeground: '0 0% 100%',
    destructive: '0 90% 50%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 85%',
    input: '0 0% 88%',
    ring: '0 90% 50%',
    sidebarBackground: '0 0% 8%',
    sidebarForeground: '0 0% 92%',
    sidebarPrimary: '0 90% 50%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '50 100% 48%',
    sidebarAccentForeground: '0 0% 5%',
    sidebarBorder: '0 0% 18%',
    sidebarRing: '0 90% 50%',
    chart1: '0 90% 50%',
    chart2: '50 100% 48%',
    chart3: '210 100% 48%',
    chart4: '0 0% 30%',
    chart5: '0 0% 60%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Inter', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0px' },           // Bauhaus: no curves
  shadows: lightShadows,
};

export const brutalismTokens: ThemeTokens = {
  colors: {
    background: '0 0% 90%',            // raw concrete
    foreground: '0 0% 3%',
    headerBackground: '0 0% 100%',
    card: '0 0% 96%',
    cardForeground: '0 0% 3%',
    popover: '0 0% 96%',
    popoverForeground: '0 0% 3%',
    primary: '0 0% 3%',                // black
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 100% 50%',
    secondary: '0 0% 82%',
    secondaryForeground: '0 0% 5%',
    muted: '0 0% 82%',
    mutedForeground: '0 0% 32%',
    accent: '0 100% 50%',              // stark red
    accentForeground: '0 0% 100%',
    destructive: '0 100% 50%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 3%',                 // thick black borders
    input: '0 0% 88%',
    ring: '0 0% 3%',
    sidebarBackground: '0 0% 12%',
    sidebarForeground: '0 0% 88%',
    sidebarPrimary: '0 0% 90%',
    sidebarPrimaryForeground: '0 0% 5%',
    sidebarAccent: '0 0% 22%',
    sidebarAccentForeground: '0 0% 95%',
    sidebarBorder: '0 0% 3%',
    sidebarRing: '0 100% 50%',
    chart1: '0 0% 3%',
    chart2: '0 100% 50%',
    chart3: '0 0% 50%',
    chart4: '0 0% 70%',
    chart5: '50 100% 48%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Space Mono', 'Courier New', monospace" },
  spacing: defaultSpacing,
  radius: { radius: '0px' },           // brutalism: hard edges
  shadows: {
    shadowSm: '2px 2px 0 0 rgb(0 0 0)',
    shadowMd: '4px 4px 0 0 rgb(0 0 0)',
    shadowLg: '6px 6px 0 0 rgb(0 0 0)',
    shadowXl: '8px 8px 0 0 rgb(0 0 0)',
  },
};

export const artDecoTokens: ThemeTokens = {
  colors: {
    background: '38 48% 8%',           // warm dark gold
    foreground: '45 75% 85%',          // gold text
    headerBackground: '38 42% 11%',
    card: '38 40% 12%',
    cardForeground: '45 70% 85%',
    popover: '38 40% 12%',
    popoverForeground: '45 70% 85%',
    primary: '45 90% 55%',             // Art Deco gold
    primaryForeground: '0 0% 5%',
    playerToggleBackground: '45 90% 55%',
    secondary: '0 0% 13%',
    secondaryForeground: '45 60% 78%',
    muted: '0 0% 12%',
    mutedForeground: '45 20% 48%',
    accent: '45 65% 42%',              // dark gold
    accentForeground: '0 0% 5%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '45 30% 18%',
    input: '0 0% 12%',
    ring: '45 90% 55%',
    sidebarBackground: '0 0% 3%',
    sidebarForeground: '45 55% 78%',
    sidebarPrimary: '45 90% 55%',
    sidebarPrimaryForeground: '0 0% 5%',
    sidebarAccent: '0 0% 10%',
    sidebarAccentForeground: '45 65% 82%',
    sidebarBorder: '45 20% 14%',
    sidebarRing: '45 80% 50%',
    chart1: '45 90% 55%',
    chart2: '35 75% 48%',
    chart3: '55 70% 52%',
    chart4: '0 0% 55%',
    chart5: '45 40% 35%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Playfair Display', serif" },
  spacing: defaultSpacing,
  radius: { radius: '0px' },
  shadows: darkShadows,
};

export const groovy70sTokens: ThemeTokens = {
  colors: {
    background: '35 40% 14%',          // warm dark brown
    foreground: '45 85% 82%',          // cream
    headerBackground: '35 40% 18%',
    card: '35 35% 18%',
    cardForeground: '45 80% 82%',
    popover: '35 35% 18%',
    popoverForeground: '45 80% 82%',
    primary: '30 80% 50%',             // burnt orange
    primaryForeground: '35 40% 10%',
    playerToggleBackground: '75 60% 42%',  // avocado green
    secondary: '35 30% 22%',
    secondaryForeground: '45 70% 78%',
    muted: '35 25% 21%',
    mutedForeground: '35 20% 52%',
    accent: '75 60% 42%',              // avocado green
    accentForeground: '35 40% 10%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '35 25% 24%',
    input: '35 30% 18%',
    ring: '30 80% 50%',
    sidebarBackground: '35 45% 9%',
    sidebarForeground: '45 65% 75%',
    sidebarPrimary: '30 80% 50%',
    sidebarPrimaryForeground: '35 40% 10%',
    sidebarAccent: '35 30% 16%',
    sidebarAccentForeground: '45 70% 80%',
    sidebarBorder: '35 22% 18%',
    sidebarRing: '30 70% 48%',
    chart1: '30 80% 50%',              // orange
    chart2: '75 60% 42%',              // avocado
    chart3: '45 85% 52%',              // mustard yellow
    chart4: '15 65% 42%',              // rust
    chart5: '55 50% 38%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Nunito', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '9999px' },        // 70s: round and groovy
  shadows: darkShadows,
};

export const neon80sTokens: ThemeTokens = {
  colors: {
    background: '300 50% 7%',          // deep magenta-black
    foreground: '0 0% 95%',
    headerBackground: '300 45% 10%',
    card: '300 42% 11%',
    cardForeground: '0 0% 95%',
    popover: '300 22% 8%',
    popoverForeground: '0 0% 95%',
    primary: '320 100% 60%',           // hot pink/magenta
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '185 100% 55%',  // neon cyan
    secondary: '300 38% 15%',
    secondaryForeground: '320 50% 80%',
    muted: '300 32% 13%',
    mutedForeground: '300 18% 52%',
    accent: '185 100% 55%',            // neon cyan
    accentForeground: '300 25% 4%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '300 35% 17%',
    input: '300 35% 12%',
    ring: '320 100% 60%',
    sidebarBackground: '300 55% 4%',
    sidebarForeground: '320 40% 78%',
    sidebarPrimary: '320 100% 60%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '300 40% 11%',
    sidebarAccentForeground: '320 50% 82%',
    sidebarBorder: '300 15% 12%',
    sidebarRing: '320 90% 58%',
    chart1: '320 100% 60%',
    chart2: '185 100% 55%',
    chart3: '265 90% 65%',
    chart4: '55 100% 50%',
    chart5: '155 90% 50%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Rajdhani', 'Inter', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: {
    shadowSm: '0 0 8px rgb(255 0 128 / 0.4)',
    shadowMd: '0 0 16px rgb(255 0 128 / 0.5), 0 0 32px rgb(0 255 255 / 0.2)',
    shadowLg: '0 0 24px rgb(255 0 128 / 0.6), 0 0 48px rgb(0 255 255 / 0.3)',
    shadowXl: '0 0 40px rgb(255 0 128 / 0.7), 0 0 80px rgb(0 255 255 / 0.4)',
  },
};

export const swissTokens: ThemeTokens = {
  colors: {
    background: '0 0% 100%',
    foreground: '0 0% 5%',
    headerBackground: '0 0% 100%',
    card: '0 0% 98%',
    cardForeground: '0 0% 5%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 5%',
    primary: '0 90% 45%',              // Swiss red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 90% 45%',
    secondary: '0 0% 93%',
    secondaryForeground: '0 0% 8%',
    muted: '0 0% 92%',
    mutedForeground: '0 0% 38%',
    accent: '0 0% 22%',               // dark grey accent
    accentForeground: '0 0% 100%',
    destructive: '0 90% 45%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 85%',
    input: '0 0% 88%',
    ring: '0 90% 45%',
    sidebarBackground: '0 0% 8%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '0 90% 45%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '0 0% 18%',
    sidebarAccentForeground: '0 0% 95%',
    sidebarBorder: '0 0% 18%',
    sidebarRing: '0 80% 42%',
    chart1: '0 90% 45%',
    chart2: '0 0% 25%',
    chart3: '0 0% 50%',
    chart4: '0 60% 50%',
    chart5: '0 0% 72%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Helvetica Neue', 'Inter', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0px' },
  shadows: lightShadows,
};

export const minimalistTokens: ThemeTokens = {
  colors: {
    background: '0 0% 99%',            // near-pure white
    foreground: '0 0% 8%',
    headerBackground: '0 0% 100%',
    card: '0 0% 100%',
    cardForeground: '0 0% 8%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 8%',
    primary: '0 0% 12%',               // near-black
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 0% 12%',
    secondary: '0 0% 96%',
    secondaryForeground: '0 0% 12%',
    muted: '0 0% 96%',
    mutedForeground: '0 0% 55%',
    accent: '0 0% 35%',               // mid-grey
    accentForeground: '0 0% 100%',
    destructive: '0 65% 48%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 92%',               // barely-there border
    input: '0 0% 94%',
    ring: '0 0% 12%',
    sidebarBackground: '0 0% 6%',
    sidebarForeground: '0 0% 88%',
    sidebarPrimary: '0 0% 94%',
    sidebarPrimaryForeground: '0 0% 6%',
    sidebarAccent: '0 0% 14%',
    sidebarAccentForeground: '0 0% 92%',
    sidebarBorder: '0 0% 14%',
    sidebarRing: '0 0% 40%',
    chart1: '0 0% 12%',
    chart2: '0 0% 35%',
    chart3: '0 0% 55%',
    chart4: '0 0% 72%',
    chart5: '0 0% 88%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Inter', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: {
    shadowSm: '0 1px 2px 0 rgb(0 0 0 / 0.03)',
    shadowMd: '0 2px 4px -1px rgb(0 0 0 / 0.06)',
    shadowLg: '0 4px 8px -2px rgb(0 0 0 / 0.08)',
    shadowXl: '0 8px 16px -4px rgb(0 0 0 / 0.08)',
  },
};

// =====================================================================
//  NATURE THEMES
// =====================================================================

export const oceanDeepTokens: ThemeTokens = {
  colors: {
    background: '218 60% 12%',         // rich deep ocean
    foreground: '195 35% 90%',
    headerBackground: '218 55% 15%',
    card: '218 52% 16%',
    cardForeground: '195 30% 90%',
    popover: '218 42% 12%',
    popoverForeground: '195 30% 90%',
    primary: '188 80% 50%',            // teal
    primaryForeground: '218 45% 6%',
    playerToggleBackground: '160 55% 45%',
    secondary: '218 48% 20%',
    secondaryForeground: '195 25% 82%',
    muted: '218 40% 18%',
    mutedForeground: '218 25% 52%',
    accent: '160 55% 45%',             // seafoam
    accentForeground: '218 45% 6%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '218 42% 22%',
    input: '218 45% 16%',
    ring: '188 80% 50%',
    sidebarBackground: '218 65% 8%',
    sidebarForeground: '195 28% 80%',
    sidebarPrimary: '188 80% 50%',
    sidebarPrimaryForeground: '218 45% 6%',
    sidebarAccent: '218 48% 15%',
    sidebarAccentForeground: '195 25% 84%',
    sidebarBorder: '218 28% 14%',
    sidebarRing: '188 70% 48%',
    chart1: '188 80% 50%',
    chart2: '160 55% 45%',
    chart3: '200 70% 55%',
    chart4: '175 60% 40%',
    chart5: '218 35% 50%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

export const forestTokens: ThemeTokens = {
  colors: {
    background: '140 50% 11%',         // rich forest green
    foreground: '90 20% 88%',
    headerBackground: '140 45% 14%',
    card: '140 42% 15%',
    cardForeground: '90 18% 88%',
    popover: '140 22% 11%',
    popoverForeground: '90 18% 88%',
    primary: '140 55% 42%',            // forest green
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '30 55% 38%',
    secondary: '140 38% 18%',
    secondaryForeground: '90 15% 80%',
    muted: '140 32% 17%',
    mutedForeground: '140 18% 50%',
    accent: '30 55% 38%',              // earthy brown
    accentForeground: '90 20% 90%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '140 35% 20%',
    input: '140 38% 15%',
    ring: '140 55% 42%',
    sidebarBackground: '140 55% 7%',
    sidebarForeground: '90 18% 78%',
    sidebarPrimary: '140 55% 42%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '140 35% 13%',
    sidebarAccentForeground: '90 15% 82%',
    sidebarBorder: '140 15% 13%',
    sidebarRing: '140 45% 38%',
    chart1: '140 55% 42%',
    chart2: '30 55% 38%',
    chart3: '90 45% 40%',
    chart4: '160 40% 38%',
    chart5: '45 55% 42%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

export const desertTokens: ThemeTokens = {
  colors: {
    background: '35 30% 93%',          // warm sand
    foreground: '25 40% 14%',
    headerBackground: '35 25% 100%',
    card: '35 22% 100%',
    cardForeground: '25 38% 14%',
    popover: '35 22% 100%',
    popoverForeground: '25 38% 14%',
    primary: '18 70% 45%',             // terracotta
    primaryForeground: '35 55% 96%',
    playerToggleBackground: '45 75% 52%',
    secondary: '35 20% 88%',
    secondaryForeground: '25 32% 18%',
    muted: '35 15% 88%',
    mutedForeground: '25 12% 48%',
    accent: '45 75% 52%',              // desert gold
    accentForeground: '25 40% 10%',
    destructive: '0 72% 45%',
    destructiveForeground: '0 0% 100%',
    border: '35 15% 82%',
    input: '35 18% 86%',
    ring: '18 70% 45%',
    sidebarBackground: '25 35% 15%',
    sidebarForeground: '35 35% 82%',
    sidebarPrimary: '18 70% 55%',
    sidebarPrimaryForeground: '35 55% 96%',
    sidebarAccent: '25 25% 22%',
    sidebarAccentForeground: '35 28% 85%',
    sidebarBorder: '25 22% 22%',
    sidebarRing: '18 60% 45%',
    chart1: '18 70% 45%',
    chart2: '45 75% 52%',
    chart3: '30 60% 48%',
    chart4: '5 55% 42%',
    chart5: '35 30% 60%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: lightShadows,
};

export const auroraTokens: ThemeTokens = {
  colors: {
    background: '245 55% 10%',         // arctic indigo night
    foreground: '170 25% 90%',
    headerBackground: '245 50% 13%',
    card: '245 48% 14%',
    cardForeground: '170 22% 90%',
    popover: '245 30% 10%',
    popoverForeground: '170 22% 90%',
    primary: '170 75% 45%',            // aurora green
    primaryForeground: '245 35% 6%',
    playerToggleBackground: '285 70% 65%',
    secondary: '245 42% 18%',
    secondaryForeground: '170 20% 82%',
    muted: '245 35% 16%',
    mutedForeground: '245 20% 52%',
    accent: '285 70% 65%',             // aurora purple
    accentForeground: '245 35% 6%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '245 38% 20%',
    input: '245 40% 14%',
    ring: '170 75% 45%',
    sidebarBackground: '245 60% 6%',
    sidebarForeground: '170 22% 78%',
    sidebarPrimary: '170 75% 45%',
    sidebarPrimaryForeground: '245 35% 5%',
    sidebarAccent: '245 42% 13%',
    sidebarAccentForeground: '170 18% 82%',
    sidebarBorder: '245 20% 12%',
    sidebarRing: '170 65% 42%',
    chart1: '170 75% 45%',
    chart2: '285 70% 65%',
    chart3: '210 70% 60%',
    chart4: '140 60% 48%',
    chart5: '260 60% 65%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

export const sunsetTokens: ThemeTokens = {
  colors: {
    background: '355 60% 10%',         // deep twilight rose
    foreground: '35 55% 90%',
    headerBackground: '355 55% 13%',
    card: '355 52% 14%',
    cardForeground: '35 50% 90%',
    popover: '355 27% 10%',
    popoverForeground: '35 50% 90%',
    primary: '25 90% 58%',             // sunset orange
    primaryForeground: '355 30% 5%',
    playerToggleBackground: '345 75% 60%',
    secondary: '355 42% 17%',
    secondaryForeground: '35 45% 82%',
    muted: '355 35% 16%',
    mutedForeground: '355 20% 52%',
    accent: '345 75% 60%',             // sunset pink
    accentForeground: '355 30% 5%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '355 38% 20%',
    input: '355 40% 15%',
    ring: '25 90% 58%',
    sidebarBackground: '355 65% 6%',
    sidebarForeground: '35 45% 78%',
    sidebarPrimary: '25 90% 58%',
    sidebarPrimaryForeground: '355 30% 5%',
    sidebarAccent: '355 40% 13%',
    sidebarAccentForeground: '35 42% 82%',
    sidebarBorder: '355 18% 13%',
    sidebarRing: '25 80% 55%',
    chart1: '25 90% 58%',
    chart2: '345 75% 60%',
    chart3: '15 80% 55%',
    chart4: '50 85% 55%',
    chart5: '280 55% 60%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

// =====================================================================
//  SPORT / TEAM COLOR THEMES
// =====================================================================

export const championshipGoldTokens: ThemeTokens = {
  colors: {
    background: '42 40% 8%',
    foreground: '45 90% 88%',
    headerBackground: '42 35% 11%',
    card: '42 32% 12%',
    cardForeground: '45 85% 88%',
    popover: '0 0% 9%',
    popoverForeground: '45 85% 88%',
    primary: '43 100% 50%',            // championship gold
    primaryForeground: '0 0% 5%',
    playerToggleBackground: '43 100% 50%',
    secondary: '42 25% 16%',
    secondaryForeground: '45 70% 80%',
    muted: '42 22% 15%',
    mutedForeground: '42 15% 52%',
    accent: '40 85% 40%',              // dark gold
    accentForeground: '45 90% 90%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '43 30% 18%',
    input: '0 0% 13%',
    ring: '43 100% 50%',
    sidebarBackground: '42 45% 5%',
    sidebarForeground: '45 70% 80%',
    sidebarPrimary: '43 100% 50%',
    sidebarPrimaryForeground: '0 0% 5%',
    sidebarAccent: '42 32% 10%',
    sidebarAccentForeground: '45 80% 84%',
    sidebarBorder: '43 20% 14%',
    sidebarRing: '43 90% 48%',
    chart1: '43 100% 50%',
    chart2: '40 85% 40%',
    chart3: '50 90% 55%',
    chart4: '0 0% 55%',
    chart5: '30 70% 45%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

export const stadiumRedTokens: ThemeTokens = {
  colors: {
    background: '0 65% 10%',
    foreground: '0 0% 95%',
    headerBackground: '0 60% 13%',
    card: '0 55% 14%',
    cardForeground: '0 0% 95%',
    popover: '0 30% 10%',
    popoverForeground: '0 0% 95%',
    primary: '0 85% 50%',              // stadium red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 85% 50%',
    secondary: '0 42% 17%',
    secondaryForeground: '0 0% 88%',
    muted: '0 35% 16%',
    mutedForeground: '0 18% 52%',
    accent: '0 60% 35%',               // dark red
    accentForeground: '0 0% 100%',
    destructive: '0 90% 55%',
    destructiveForeground: '0 0% 100%',
    border: '0 40% 20%',
    input: '0 42% 15%',
    ring: '0 85% 50%',
    sidebarBackground: '0 70% 6%',
    sidebarForeground: '0 0% 82%',
    sidebarPrimary: '0 85% 50%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '0 40% 13%',
    sidebarAccentForeground: '0 0% 88%',
    sidebarBorder: '0 35% 15%',
    sidebarRing: '0 75% 48%',
    chart1: '0 85% 50%',
    chart2: '0 60% 35%',
    chart3: '15 70% 48%',
    chart4: '0 0% 60%',
    chart5: '350 55% 42%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

export const electricBlueTokens: ThemeTokens = {
  colors: {
    background: '220 70% 11%',
    foreground: '0 0% 96%',
    headerBackground: '220 65% 14%',
    card: '220 62% 15%',
    cardForeground: '0 0% 96%',
    popover: '220 42% 11%',
    popoverForeground: '0 0% 96%',
    primary: '215 100% 55%',           // electric blue
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '215 100% 55%',
    secondary: '220 52% 18%',
    secondaryForeground: '0 0% 88%',
    muted: '220 45% 17%',
    mutedForeground: '220 22% 52%',
    accent: '235 80% 60%',             // deep blue
    accentForeground: '0 0% 100%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '220 45% 20%',
    input: '220 50% 15%',
    ring: '215 100% 55%',
    sidebarBackground: '220 75% 7%',
    sidebarForeground: '0 0% 82%',
    sidebarPrimary: '215 100% 55%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '220 52% 14%',
    sidebarAccentForeground: '0 0% 88%',
    sidebarBorder: '220 25% 14%',
    sidebarRing: '215 90% 52%',
    chart1: '215 100% 55%',
    chart2: '235 80% 60%',
    chart3: '200 80% 55%',
    chart4: '245 70% 62%',
    chart5: '220 30% 50%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

export const pitchGreenTokens: ThemeTokens = {
  colors: {
    background: '150 65% 10%',
    foreground: '0 0% 95%',
    headerBackground: '150 60% 13%',
    card: '150 58% 14%',
    cardForeground: '0 0% 95%',
    popover: '150 38% 10%',
    popoverForeground: '0 0% 95%',
    primary: '150 60% 40%',            // pitch green
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '43 95% 52%',
    secondary: '150 48% 17%',
    secondaryForeground: '0 0% 88%',
    muted: '150 40% 16%',
    mutedForeground: '150 20% 52%',
    accent: '43 95% 52%',              // gold
    accentForeground: '150 40% 5%',
    destructive: '0 72% 50%',
    destructiveForeground: '0 0% 100%',
    border: '150 45% 20%',
    input: '150 48% 15%',
    ring: '150 60% 40%',
    sidebarBackground: '150 70% 6%',
    sidebarForeground: '0 0% 82%',
    sidebarPrimary: '150 60% 40%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '150 48% 13%',
    sidebarAccentForeground: '0 0% 88%',
    sidebarBorder: '150 22% 13%',
    sidebarRing: '150 50% 38%',
    chart1: '150 60% 40%',
    chart2: '43 95% 52%',
    chart3: '120 50% 40%',
    chart4: '170 45% 38%',
    chart5: '150 25% 48%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  ADDITIONAL COUNTRY / FLAG THEMES
// =====================================================================

export const brazilianTokens: ThemeTokens = {
  colors: {
    background: '145 60% 10%',         // deep Amazon green
    foreground: '50 90% 88%',          // golden yellow text
    headerBackground: '145 60% 13%',
    card: '145 55% 14%',
    cardForeground: '50 85% 88%',
    popover: '145 55% 14%',
    popoverForeground: '50 85% 88%',
    primary: '50 100% 50%',            // Brazil yellow
    primaryForeground: '145 60% 10%',
    playerToggleBackground: '213 80% 50%', // Brazil blue
    secondary: '145 42% 18%',
    secondaryForeground: '50 75% 82%',
    muted: '145 35% 17%',
    mutedForeground: '145 20% 52%',
    accent: '213 80% 50%',             // Brazil blue
    accentForeground: '0 0% 100%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '145 35% 20%',
    input: '145 40% 15%',
    ring: '50 100% 50%',
    sidebarBackground: '145 65% 7%',
    sidebarForeground: '50 75% 80%',
    sidebarPrimary: '50 100% 50%',
    sidebarPrimaryForeground: '145 60% 8%',
    sidebarAccent: '145 42% 14%',
    sidebarAccentForeground: '50 80% 84%',
    sidebarBorder: '145 35% 16%',
    sidebarRing: '50 90% 48%',
    chart1: '50 100% 50%',
    chart2: '213 80% 50%',
    chart3: '145 60% 40%',
    chart4: '0 80% 55%',
    chart5: '145 30% 48%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

export const germanTokens: ThemeTokens = {
  colors: {
    background: '0 28% 9%',            // dark red-charcoal (German flag)
    foreground: '0 0% 95%',
    headerBackground: '0 24% 12%',
    card: '0 22% 13%',
    cardForeground: '0 0% 95%',
    popover: '0 0% 10%',
    popoverForeground: '0 0% 95%',
    primary: '0 85% 50%',              // German red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '45 100% 50%',  // German gold
    secondary: '0 0% 15%',
    secondaryForeground: '0 0% 88%',
    muted: '0 0% 14%',
    mutedForeground: '0 0% 52%',
    accent: '45 100% 50%',             // German gold
    accentForeground: '0 0% 6%',
    destructive: '0 90% 55%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 18%',
    input: '0 0% 13%',
    ring: '0 85% 50%',
    sidebarBackground: '0 0% 3%',
    sidebarForeground: '0 0% 85%',
    sidebarPrimary: '0 85% 50%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '0 0% 11%',
    sidebarAccentForeground: '0 0% 90%',
    sidebarBorder: '0 0% 13%',
    sidebarRing: '0 75% 48%',
    chart1: '0 0% 10%',
    chart2: '0 85% 50%',
    chart3: '45 100% 50%',
    chart4: '0 0% 55%',
    chart5: '15 70% 45%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: darkShadows,
};

export const nigerianTokens: ThemeTokens = {
  colors: {
    background: '145 58% 18%',         // vivid dark green (Nigeria flag)
    foreground: '0 0% 96%',
    headerBackground: '145 55% 42%',   // bright Nigerian green header
    card: '145 50% 24%',
    cardForeground: '0 0% 96%',
    popover: '145 50% 24%',
    popoverForeground: '0 0% 96%',
    primary: '145 55% 42%',            // Nigerian green button
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 0% 95%', // white accent
    secondary: '145 42% 28%',
    secondaryForeground: '0 0% 95%',
    muted: '145 38% 22%',
    mutedForeground: '145 25% 65%',    // green-tinted muted text
    accent: '0 0% 95%',                // white (Nigerian flag)
    accentForeground: '145 58% 18%',
    destructive: '0 80% 55%',
    destructiveForeground: '0 0% 100%',
    border: '145 35% 30%',
    input: '145 40% 22%',
    ring: '145 55% 42%',
    sidebarBackground: '145 60% 12%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '145 55% 42%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '145 42% 20%',
    sidebarAccentForeground: '0 0% 92%',
    sidebarBorder: '145 35% 22%',
    sidebarRing: '145 45% 38%',
    chart1: '145 55% 42%',
    chart2: '0 0% 85%',
    chart3: '120 55% 45%',
    chart4: '160 50% 50%',
    chart5: '145 40% 60%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.15rem' },
  shadows: darkShadows,
};

export const americanTokens: ThemeTokens = {
  colors: {
    background: '220 70% 12%',         // rich navy
    foreground: '0 0% 97%',
    headerBackground: '220 65% 15%',
    card: '220 62% 16%',
    cardForeground: '0 0% 97%',
    popover: '220 50% 12%',
    popoverForeground: '0 0% 97%',
    primary: '0 80% 50%',              // American red
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '0 80% 50%',
    secondary: '220 38% 18%',
    secondaryForeground: '0 0% 88%',
    muted: '220 30% 16%',
    mutedForeground: '220 18% 52%',
    accent: '0 0% 96%',                // white star
    accentForeground: '220 55% 8%',
    destructive: '0 85% 55%',
    destructiveForeground: '0 0% 100%',
    border: '220 30% 20%',
    input: '220 35% 14%',
    ring: '0 80% 50%',
    sidebarBackground: '220 60% 5%',
    sidebarForeground: '0 0% 85%',
    sidebarPrimary: '0 80% 50%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '220 38% 13%',
    sidebarAccentForeground: '0 0% 90%',
    sidebarBorder: '220 28% 15%',
    sidebarRing: '0 70% 48%',
    chart1: '0 80% 50%',
    chart2: '0 0% 95%',
    chart3: '220 55% 50%',
    chart4: '215 70% 55%',
    chart5: '220 30% 48%',
  },
  typography: defaultTypo,
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  STREAMING PLATFORM THEMES
// =====================================================================

// Spotify-like Theme (Dark with green accents)
export const spotifyTokens: ThemeTokens = {
  colors: {
    background: '141 25% 8%',          // dark green (Spotify-like)
    foreground: '0 0% 100%',
    headerBackground: '141 22% 11%',
    card: '141 20% 13%',
    cardForeground: '0 0% 100%',
    popover: '0 0% 12%',
    popoverForeground: '0 0% 100%',
    primary: '141 76% 48%',
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '141 76% 48%',
    secondary: '141 16% 20%',
    secondaryForeground: '0 0% 100%',
    muted: '141 14% 18%',
    mutedForeground: '141 8% 70%',
    accent: '141 76% 48%',
    accentForeground: '0 0% 100%',
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 18%',
    input: '0 0% 18%',
    ring: '141 76% 48%',
    sidebarBackground: '0 0% 0%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '141 76% 48%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '0 0% 15%',
    sidebarAccentForeground: '0 0% 100%',
    sidebarBorder: '0 0% 15%',
    sidebarRing: '141 76% 48%',
    chart1: '141 76% 48%',
    chart2: '141 55% 34%',   // greens only — Spotify is green, dark green and white
    chart3: '141 35% 24%',
    chart4: '141 30% 70%',
    chart5: '0 0% 70%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Montserrat', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.075rem' },
  shadows: darkShadows,
};

// Apple Music-like Theme (Light with red/pink accents)
export const appleMusicTokens: ThemeTokens = {
  colors: {
    background: '0 0% 98%',
    foreground: '0 0% 13%',
    headerBackground: '0 0% 100%',
    card: '0 0% 100%',
    cardForeground: '0 0% 13%',
    popover: '0 0% 100%',
    popoverForeground: '0 0% 13%',
    primary: '352 84% 55%',
    primaryForeground: '0 0% 100%',
    playerToggleBackground: '352 84% 55%',
    secondary: '0 0% 96%',
    secondaryForeground: '0 0% 13%',
    muted: '0 0% 96%',
    mutedForeground: '0 0% 45%',
    accent: '352 84% 55%',
    accentForeground: '0 0% 100%',
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border: '0 0% 90%',
    input: '0 0% 90%',
    ring: '352 84% 55%',
    sidebarBackground: '0 0% 96%',
    sidebarForeground: '0 0% 13%',
    sidebarPrimary: '352 84% 55%',
    sidebarPrimaryForeground: '0 0% 100%',
    sidebarAccent: '0 0% 92%',
    sidebarAccentForeground: '0 0% 13%',
    sidebarBorder: '0 0% 90%',
    sidebarRing: '352 84% 55%',
    chart1: '352 84% 55%',
    chart2: '10 80% 50%',
    chart3: '40 90% 50%',
    chart4: '200 70% 50%',
    chart5: '280 70% 60%',
  },
  typography: { ...defaultTypo, fontGlobal: "'SF Pro Display', -apple-system, sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.225rem' },
  shadows: lightShadows,
};

// TIDAL-like Theme (Dark with cyan/blue accents)
export const tidalTokens: ThemeTokens = {
  colors: {
    background: '210 48% 10%',         // rich ocean blue (TIDAL-like)
    foreground: '0 0% 98%',
    headerBackground: '210 44% 13%',
    card: '210 42% 14%',
    cardForeground: '0 0% 98%',
    popover: '210 25% 12%',
    popoverForeground: '0 0% 98%',
    primary: '187 85% 53%',
    primaryForeground: '210 25% 8%',
    playerToggleBackground: '187 85% 53%',
    secondary: '210 38% 20%',
    secondaryForeground: '0 0% 98%',
    muted: '210 35% 18%',
    mutedForeground: '210 15% 70%',
    accent: '187 85% 53%',
    accentForeground: '210 25% 8%',
    destructive: '0 84% 60%',
    destructiveForeground: '0 0% 100%',
    border: '210 25% 20%',
    input: '210 25% 18%',
    ring: '187 85% 53%',
    sidebarBackground: '210 25% 6%',
    sidebarForeground: '0 0% 90%',
    sidebarPrimary: '187 85% 53%',
    sidebarPrimaryForeground: '210 25% 8%',
    sidebarAccent: '210 25% 15%',
    sidebarAccentForeground: '0 0% 98%',
    sidebarBorder: '210 25% 15%',
    sidebarRing: '187 85% 53%',
    chart1: '187 85% 53%',
    chart2: '200 80% 60%',
    chart3: '220 70% 65%',
    chart4: '240 60% 60%',
    chart5: '260 70% 65%',
  },
  typography: { ...defaultTypo, fontGlobal: "'Poppins', sans-serif" },
  spacing: defaultSpacing,
  radius: { radius: '0.1rem' },
  shadows: darkShadows,
};

// =====================================================================
//  PRESETS ARRAY  –  order shown in theme picker
// =====================================================================
export type ThemeCategory =
  | 'system'
  | 'accessibility'
  | 'bb'
  | 'music'
  | 'design'
  | 'nature'
  | 'sport'
  | 'country'
  | 'platform';

export const THEME_CATEGORIES: { id: ThemeCategory; label: string; emoji: string }[] = [
  { id: 'system',        label: 'System',        emoji: '🖥️' },
  { id: 'accessibility', label: 'Accessibility', emoji: '♿' },
  { id: 'bb',            label: 'BB',            emoji: '🍞' },
  { id: 'music',         label: 'Music',         emoji: '🎵' },
  { id: 'design',        label: 'Design',        emoji: '🎨' },
  { id: 'nature',        label: 'Nature',        emoji: '🌿' },
  { id: 'sport',         label: 'Sport',         emoji: '🏆' },
  { id: 'country',       label: 'Countries',     emoji: '🌍' },
  { id: 'platform',      label: 'Platforms',     emoji: '🎧' },
];

// =====================================================================
//  Helper: Generate country flag theme presets
// =====================================================================
/**
 * Extract just the hue from an "H S% L%" string, e.g. "140 70% 30%" → 140.
 * Falls back to 0 for achromatic values like "0 0% 8%".
 */
function extractHue(hsl: string): number {
  const h = parseInt(hsl.trim().split(/\s+/)[0], 10);
  return isNaN(h) ? 0 : h;
}
function extractSat(hsl: string): number {
  const p = hsl.trim().split(/\s+/);
  const s = parseFloat(p[1] ?? '0');
  return isNaN(s) ? 0 : s;
}

function makeCountryTheme(
  id: string,
  name: string,
  description: string,
  primary: string,
  primaryFg: string,
  accent: string,
  accentFg: string,
  sidebar: string
): { id: string; name: string; description: string; category: 'country'; tokens: typeof plainLightTokens; menuStyle: { defaultView: 'grid' } } {
  // ── Background: vivid dark version of the flag's dominant colour ────────────
  // Saturation stays high (45-72%) so each country is unmistakeable even at
  // dark lightness. Achromatic primaries (e.g. black flags) fall back to the
  // accent hue so they're still distinctly coloured.
  const primarySat = extractSat(primary);
  const accentSat  = extractSat(accent);
  const bgHue = primarySat >= 5 ? extractHue(primary) : extractHue(accent);
  const bgSat = primarySat >= 5
    ? Math.min(72, Math.max(45, Math.round(primarySat * 0.82)))
    : Math.min(62, Math.max(38, Math.round(accentSat * 0.78)));

  // 22% lightness: dark, but the colour is clearly legible at thumbnail size
  const bg          = `${bgHue} ${bgSat}% 26%`;
  // All surfaces share the same hue — no neutral grays
  const card        = `${bgHue} ${Math.max(38, bgSat - 8)}% 32%`;
  const muted       = `${bgHue} ${Math.max(32, bgSat - 12)}% 30%`;
  const border      = `${bgHue} ${Math.max(25, bgSat - 20)}% 38%`;
  const sidebarBg   = `${bgHue} ${bgSat}% 20%`;   // slightly darker than bg
  const mutedFg     = `${bgHue} 30% 65%`;          // hue-tinted muted text

  // Foreground: accent when it's bright (≥45% L), otherwise near-white
  const accentL = parseFloat((accent.trim().split(/\s+/)[2] ?? '50').replace('%', ''));
  const fg = accentL >= 45 ? accent : '0 0% 95%';

  return {
    id,
    name,
    description,
    category: 'country' as const,
    tokens: {
      colors: {
        background:               bg,
        foreground:               fg,
        headerBackground:         primary,
        card,
        cardForeground:           fg,
        popover:                  card,
        popoverForeground:        fg,
        primary,
        primaryForeground:        primaryFg,
        playerToggleBackground:   accent,
        secondary:                muted,
        secondaryForeground:      fg,
        muted,
        mutedForeground:          mutedFg,
        accent,
        accentForeground:         accentFg,
        destructive:              '0 72% 51%',
        destructiveForeground:    '0 0% 98%',
        border,
        input:                    border,
        ring:                     primary,
        sidebarBackground:        sidebarBg,
        sidebarForeground:        fg,
        sidebarPrimary:           primary,
        sidebarPrimaryForeground: primaryFg,
        sidebarAccent:            accent,
        sidebarAccentForeground:  accentFg,
        sidebarBorder:            border,
        sidebarRing:              primary,
        chart1: primary,
        chart2: accent,
        chart3: `${bgHue} 55% 55%`,
        chart4: `${bgHue} 40% 65%`,
        chart5: `${bgHue} 70% 45%`,
      },
      typography: plainLightTokens.typography,
      spacing:    plainLightTokens.spacing,
      radius:     plainLightTokens.radius,
      shadows:    plainLightTokens.shadows,
    },
    menuStyle: { defaultView: 'grid' },
  };
}

export const themePresets: {
  id: string;
  name: string;
  description: string;
  category: ThemeCategory;
  tokens: typeof plainLightTokens;
  darkTokens?: typeof plainDarkTokens;
  lightTokens?: typeof plainLightTokens;
  menuStyle: { defaultView: 'grid' | 'list' };
}[] = [
  // ── System / B&W ──────────────────────────────────────────────────
  { id: 'system',      name: 'System',      category: 'system',        description: 'Follows your device light/dark mode',                                  tokens: plainLightTokens,       darkTokens: plainDarkTokens,    menuStyle: { defaultView: 'grid' } },
  { id: 'plain-light', name: 'Light',       category: 'system',        description: 'Pure white bg, pure black text — maximum contrast',                    tokens: plainLightTokens,       darkTokens: plainDarkTokens,    menuStyle: { defaultView: 'grid' } },
  { id: 'plain-dark',  name: 'Dark',        category: 'system',        description: 'Graphite background, pure white text — neutral dark mode',                    tokens: plainDarkTokens,        lightTokens: plainLightTokens,  menuStyle: { defaultView: 'grid' } },
  // ── Accessibility ──────────────────────────────────────────────────
  { id: 'accessible-light', name: 'High Contrast Light', category: 'accessibility', description: 'Black on white — 21:1 ratio, oversized text (WCAG AAA). Best for most low-vision users.',   tokens: accessibleLightTokens,  menuStyle: { defaultView: 'list' } },
  { id: 'accessible-dark',  name: 'High Contrast Dark',  category: 'accessibility', description: 'White on black — 21:1 ratio, oversized text. Better for photosensitivity / light sensitivity.', tokens: accessibleDarkTokens,   menuStyle: { defaultView: 'list' } },
  { id: 'low-vision',       name: 'Low Vision',          category: 'accessibility', description: 'Yellow and white on black, extra-large easy-to-read letters, no see-through glass, big focus rings and no motion.', tokens: lowVisionTokens, menuStyle: { defaultView: 'list' } },
  // ── BB Branded ─────────────────────────────────────────────────────
  { id: 'bb-light',    name: 'BB Light',    category: 'bb',     description: 'Light theme with purple accents',                            tokens: bbLightTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'bb-dark',     name: 'BB Dark',     category: 'bb',     description: 'Dark theme with purple accents',                             tokens: bbDarkTokens,                                                         menuStyle: { defaultView: 'grid' } },
  // ── Music Genres ───────────────────────────────────────────────────
  { id: 'hip-hop',     name: 'Hip Hop',     category: 'music',  description: 'Gold on black — street royalty',                             tokens: hipHopTokens,                                                         menuStyle: { defaultView: 'grid' } },
  { id: 'jazz',        name: 'Jazz',        category: 'music',  description: 'Navy & gold — New Orleans late nights',                      tokens: jazzTokens,                                                           menuStyle: { defaultView: 'grid' } },
  { id: 'classical',   name: 'Classical',   category: 'music',  description: 'Ivory & burgundy — concert hall elegance',                   tokens: classicalTokens,                                                      menuStyle: { defaultView: 'grid' } },
  { id: 'electronic',  name: 'Electronic',  category: 'music',  description: 'Neon cyan & purple — club & rave',                           tokens: electronicTokens,                                                     menuStyle: { defaultView: 'grid' } },
  { id: 'rock',        name: 'Rock',        category: 'music',  description: 'Red & steel on black — power chords',                        tokens: rockTokens,                                                           menuStyle: { defaultView: 'list' } },
  { id: 'rnb',         name: 'R&B / Soul',  category: 'music',  description: 'Deep plum & rose — velvet soul',                            tokens: rnbTokens,                                                            menuStyle: { defaultView: 'grid' } },
  { id: 'blues',       name: 'Blues',       category: 'music',  description: 'Steel blue — twelve-bar heartache',                          tokens: bluesTokens,                                                          menuStyle: { defaultView: 'grid' } },
  { id: 'country',     name: 'Country',     category: 'music',  description: 'Warm leather & hay gold — open road',                        tokens: countryTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'reggae',      name: 'Reggae',      category: 'music',  description: 'Red, gold & green — one love vibes',                        tokens: africanTokens,                                                         menuStyle: { defaultView: 'grid' } },
  // ── Design Styles ──────────────────────────────────────────────────
  { id: 'minimalism',  name: 'Minimalism',  category: 'design', description: 'Ultra-clean white space — less is more',                    tokens: minimalistTokens,                                                     menuStyle: { defaultView: 'grid' } },
  { id: 'bauhaus',     name: 'Bauhaus',     category: 'design', description: 'Primary colours on white — form follows function',           tokens: bauhausTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'brutalism',   name: 'Brutalism',   category: 'design', description: 'Raw concrete & stark black — no compromises',               tokens: brutalismTokens,                                                      menuStyle: { defaultView: 'list' } },
  { id: 'art-deco',    name: 'Art Deco',    category: 'design', description: 'Gold on black — geometric glamour',                          tokens: artDecoTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: '70s-groove',  name: '70s Groove',  category: 'design', description: 'Burnt orange & avocado — funky & far out',                  tokens: groovy70sTokens,                                                      menuStyle: { defaultView: 'grid' } },
  { id: '80s-neon',    name: '80s Neon',    category: 'design', description: 'Hot pink & cyan — synth wave',                              tokens: neon80sTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'swiss',       name: 'Swiss',       category: 'design', description: 'Clean red & black — International Typographic Style',        tokens: swissTokens,                                                          menuStyle: { defaultView: 'grid' } },
  { id: 'retro-terminal', name: 'Terminal', category: 'design', description: 'Phosphor green on black — classic CRT',                     tokens: retroTerminalTokens,                                                  menuStyle: { defaultView: 'list' } },
  // ── Nature ──────────────────────────────────────────────────────────
  { id: 'ocean-deep',  name: 'Ocean',       category: 'nature', description: 'Deep navy & teal — beneath the waves',                      tokens: oceanDeepTokens,                                                      menuStyle: { defaultView: 'grid' } },
  { id: 'forest',      name: 'Forest',      category: 'nature', description: 'Deep green & earthy brown — old growth',                    tokens: forestTokens,                                                         menuStyle: { defaultView: 'grid' } },
  { id: 'desert',      name: 'Desert',      category: 'nature', description: 'Sand & terracotta — golden hour',                           tokens: desertTokens,                                                         menuStyle: { defaultView: 'grid' } },
  { id: 'aurora',      name: 'Aurora',      category: 'nature', description: 'Arctic night with northern lights',                          tokens: auroraTokens,                                                         menuStyle: { defaultView: 'grid' } },
  { id: 'sunset',      name: 'Sunset',      category: 'nature', description: 'Orange & pink — golden hour glow',                          tokens: sunsetTokens,                                                         menuStyle: { defaultView: 'grid' } },
  // ── Sport ───────────────────────────────────────────────────────────
  { id: 'champ-gold',  name: 'Championship Gold', category: 'sport', description: 'Black & gold — trophy winner',                         tokens: championshipGoldTokens,                                               menuStyle: { defaultView: 'grid' } },
  { id: 'stadium-red', name: 'Stadium Red',  category: 'sport', description: 'Deep red — home crowd roar',                                tokens: stadiumRedTokens,                                                     menuStyle: { defaultView: 'grid' } },
  { id: 'electric-blue', name: 'Electric Blue', category: 'sport', description: 'Electric blue — lightning fast',                         tokens: electricBlueTokens,                                                   menuStyle: { defaultView: 'grid' } },
  { id: 'pitch-green', name: 'Pitch Green', category: 'sport', description: 'Green & gold — game on',                                     tokens: pitchGreenTokens,                                                     menuStyle: { defaultView: 'grid' } },
  // ── Countries ───────────────────────────────────────────────────────
  { id: 'african',     name: 'Pan-African', category: 'country', description: 'Red, gold & green — Pan-African colours',                  tokens: africanTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'jamaican',    name: 'Jamaican',    category: 'country', description: 'Black, yellow & green — Jamaican flag',                    tokens: jamaicanTokens,                                                       menuStyle: { defaultView: 'grid' } },
  { id: 'french',      name: 'French',      category: 'country', description: 'Blue, white & red — Le Tricolore',                         tokens: frenchTokens,                                                         menuStyle: { defaultView: 'grid' } },
  { id: 'japanese',    name: 'Japanese',    category: 'country', description: 'White & red — Hinomaru',                                   tokens: japaneseTokens,                                                       menuStyle: { defaultView: 'grid' } },
  { id: 'swedish',     name: 'Swedish',     category: 'country', description: 'Yellow & blue — Blågult',                                  tokens: swedishTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'chinese',     name: 'Chinese',     category: 'country', description: 'Red & gold — imperial colours',                            tokens: chineseTokens,                                                        menuStyle: { defaultView: 'grid' } },
  { id: 'brazilian',   name: 'Brazilian',   category: 'country', description: 'Green, yellow & blue — Ordem e Progresso',                 tokens: brazilianTokens,                                                      menuStyle: { defaultView: 'grid' } },
  { id: 'german',      name: 'German',      category: 'country', description: 'Black, red & gold — Schwarz-Rot-Gold',                     tokens: germanTokens,                                                         menuStyle: { defaultView: 'grid' } },
  { id: 'nigerian',    name: 'Nigerian',    category: 'country', description: 'Green & white — Nigerian flag',                            tokens: nigerianTokens,                                                       menuStyle: { defaultView: 'grid' } },
  { id: 'american',    name: 'American',    category: 'country', description: 'Navy, red & white — Stars and Stripes',                    tokens: americanTokens,                                                       menuStyle: { defaultView: 'grid' } },
  // ── Platform-inspired ──────────────────────────────────────────────
  { id: 'spotify',     name: 'Spotify',     category: 'platform', description: 'Dark with green accents',                                  tokens: spotifyTokens,                                                        menuStyle: { defaultView: 'list' } },
  { id: 'apple-music', name: 'Apple Music', category: 'platform', description: 'Light and clean with red/pink accents',                   tokens: appleMusicTokens,                                                     menuStyle: { defaultView: 'grid' } },
  { id: 'tidal',       name: 'TIDAL',       category: 'platform', description: 'Dark with cyan accents',                                   tokens: tidalTokens,                                                          menuStyle: { defaultView: 'list' } },
  // ── Countries ─────────────────────────────────────────────────────────────
  makeCountryTheme('algeria', 'Algeria 🇩🇿', 'Green and white with red crescent', '140 70% 30%', '0 0% 98%', '0 85% 45%', '0 0% 98%', '140 70% 20%'),
  makeCountryTheme('angola', 'Angola 🇦🇴', 'Red and black with yellow emblem', '0 80% 40%', '0 0% 98%', '45 90% 50%', '0 0% 5%', '0 0% 10%'),
  makeCountryTheme('benin', 'Benin 🇧🇯', 'Green, yellow, red pan-African', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('botswana', 'Botswana 🇧🇼', 'Light blue with white and black bands', '200 70% 52%', '0 0% 5%', '0 0% 10%', '0 0% 98%', '200 70% 35%'),
  makeCountryTheme('burkina_faso', 'Burkina Faso 🇧🇫', 'Red, green, yellow pan-African', '0 80% 40%', '0 0% 98%', '140 65% 35%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('burundi', 'Burundi 🇧🇮', 'Red, green, white with stars', '0 80% 40%', '0 0% 98%', '140 65% 35%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('cabo_verde', 'Cabo Verde 🇨🇻', 'Blue with red and white stripes and stars', '220 80% 42%', '0 0% 98%', '0 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('cameroon', 'Cameroon 🇨🇲', 'Green, red, yellow with star', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('central_african_republic', 'Central African Republic 🇨🇫', 'Blue, white, green, yellow, red with star', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('chad', 'Chad 🇹🇩', 'Blue, yellow, red — nearly identical to Romania', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('comoros', 'Comoros 🇰🇲', 'Green with white crescent and stars on blue stripes', '140 65% 30%', '0 0% 98%', '220 80% 45%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('dr_congo', 'DR Congo 🇨🇩', 'Sky blue with red diagonal and yellow star', '200 75% 48%', '0 0% 5%', '0 80% 42%', '0 0% 98%', '200 75% 30%'),
  makeCountryTheme('republic_congo', 'Republic of Congo 🇨🇬', 'Green, yellow, red diagonal', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('djibouti', 'Djibouti 🇩🇯', 'Light blue, green, white with red star', '200 75% 52%', '0 0% 5%', '140 65% 35%', '0 0% 98%', '200 75% 35%'),
  makeCountryTheme('egypt', 'Egypt 🇪🇬', 'Red, white, black with golden eagle', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('equatorial_guinea', 'Equatorial Guinea 🇬🇶', 'Green, white, red with blue triangle', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('eritrea', 'Eritrea 🇪🇷', 'Green, blue, red with olive branch', '140 65% 30%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('eswatini', 'Eswatini 🇸🇿', 'Blue, yellow, red with shield', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('ethiopia', 'Ethiopia 🇪🇹', 'Green, yellow, red with blue star', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('gabon', 'Gabon 🇬🇦', 'Green, yellow, blue tricolor', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('gambia', 'Gambia 🇬🇲', 'Red, blue, green with white stripes', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('ghana', 'Ghana 🇬🇭', 'Red, gold, green with black star', '0 80% 40%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('guinea', 'Guinea 🇬🇳', 'Red, yellow, green pan-African', '0 80% 40%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('guinea_bissau', 'Guinea-Bissau 🇬🇼', 'Red, yellow, green with black star', '0 80% 40%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('ivory_coast', 'Ivory Coast 🇨🇮', 'Orange, white, green tricolor', '25 90% 55%', '0 0% 5%', '140 65% 35%', '0 0% 98%', '25 90% 38%'),
  makeCountryTheme('kenya', 'Kenya 🇰🇪', 'Black, red, green with Maasai shield', '0 0% 8%', '0 0% 95%', '0 80% 40%', '0 0% 98%', '0 0% 5%'),
  makeCountryTheme('lesotho', 'Lesotho 🇱🇸', 'Blue, white, green with Basotho hat', '220 80% 42%', '0 0% 98%', '140 65% 35%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('liberia', 'Liberia 🇱🇷', 'Red and white stripes with blue canton and star', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('libya', 'Libya 🇱🇾', 'Red, black, green with white crescent and star', '0 0% 8%', '0 0% 95%', '140 65% 30%', '0 0% 98%', '0 0% 5%'),
  makeCountryTheme('madagascar', 'Madagascar 🇲🇬', 'Red, green, white', '0 80% 40%', '0 0% 98%', '140 65% 35%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('malawi', 'Malawi 🇲🇼', 'Black, red, green with rising sun', '0 0% 8%', '0 0% 95%', '0 80% 40%', '0 0% 98%', '0 0% 5%'),
  makeCountryTheme('mali', 'Mali 🇲🇱', 'Green, gold, red pan-African', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('mauritania', 'Mauritania 🇲🇷', 'Green with gold crescent and red stripes', '140 65% 25%', '0 0% 98%', '45 85% 50%', '0 0% 5%', '140 65% 18%'),
  makeCountryTheme('mauritius', 'Mauritius 🇲🇺', 'Red, blue, yellow, green quarters', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('morocco', 'Morocco 🇲🇦', 'Red with green pentagram', '0 80% 40%', '0 0% 98%', '140 65% 35%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('mozambique', 'Mozambique 🇲🇿', 'Teal, black, yellow, white, red with AK-47', '175 60% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '175 60% 20%'),
  makeCountryTheme('namibia', 'Namibia 🇳🇦', 'Blue, red, green with sun', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('niger', 'Niger 🇳🇪', 'Orange, white, green with orange circle', '25 90% 55%', '0 0% 5%', '140 65% 35%', '0 0% 98%', '25 90% 38%'),
  makeCountryTheme('rwanda', 'Rwanda 🇷🇼', 'Blue, yellow, green with sun', '220 80% 50%', '0 0% 5%', '45 90% 55%', '0 0% 5%', '220 80% 35%'),
  makeCountryTheme('sao_tome_principe', 'São Tomé & Príncipe 🇸🇹', 'Green, yellow, red with black stars', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('senegal', 'Senegal 🇸🇳', 'Green, yellow, red with green star', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('seychelles', 'Seychelles 🇸🇨', 'Blue, yellow, red, white, green rays', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('sierra_leone', 'Sierra Leone 🇸🇱', 'Green, white, blue', '140 65% 30%', '0 0% 98%', '220 80% 50%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('somalia', 'Somalia 🇸🇴', 'Sky blue with white star', '210 80% 50%', '0 0% 5%', '0 0% 98%', '0 0% 5%', '210 80% 35%'),
  makeCountryTheme('south_africa', 'South Africa 🇿🇦', 'Green Y-shape on black, gold, red, white, blue', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('south_sudan', 'South Sudan 🇸🇸', 'Black, red, green with blue triangle and star', '0 0% 8%', '0 0% 95%', '0 80% 40%', '0 0% 98%', '0 0% 5%'),
  makeCountryTheme('sudan', 'Sudan 🇸🇩', 'Red, white, black with green triangle', '0 80% 40%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('tanzania', 'Tanzania 🇹🇿', 'Green, black, blue diagonal with gold borders', '140 65% 30%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('togo', 'Togo 🇹🇬', 'Green and yellow stripes with red canton and white star', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('tunisia', 'Tunisia 🇹🇳', 'Red with white circle, red crescent and star', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('uganda', 'Uganda 🇺🇬', 'Black, yellow, red with grey crowned crane', '0 0% 8%', '0 0% 95%', '45 90% 52%', '0 0% 5%', '0 0% 5%'),
  makeCountryTheme('zambia', 'Zambia 🇿🇲', 'Green with orange eagle and red, black, orange stripes', '140 65% 30%', '0 0% 98%', '25 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('zimbabwe', 'Zimbabwe 🇿🇼', 'Green, yellow, red, black with white triangle and bird', '140 65% 30%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('antigua_barbuda', 'Antigua & Barbuda 🇦🇬', 'Black, blue, white, yellow, red with sun', '0 0% 8%', '0 0% 95%', '220 80% 50%', '0 0% 98%', '0 0% 5%'),
  makeCountryTheme('argentina', 'Argentina 🇦🇷', 'Light blue and white with sun of May', '200 75% 55%', '0 0% 5%', '0 0% 98%', '0 0% 5%', '200 75% 35%'),
  makeCountryTheme('bahamas', 'Bahamas 🇧🇸', 'Aquamarine, gold, black triangle', '175 70% 42%', '0 0% 5%', '45 85% 50%', '0 0% 5%', '175 70% 28%'),
  makeCountryTheme('barbados', 'Barbados 🇧🇧', 'Ultramarine blue and gold with broken trident', '240 70% 45%', '0 0% 98%', '45 85% 50%', '0 0% 5%', '240 70% 30%'),
  makeCountryTheme('belize', 'Belize 🇧🇿', 'Blue, red with coat of arms on white circle', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('bolivia', 'Bolivia 🇧🇴', 'Red, yellow, green tricolor', '0 80% 40%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('canada', 'Canada 🇨🇦', 'Red and white with maple leaf', '0 85% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 85% 28%'),
  makeCountryTheme('chile', 'Chile 🇨🇱', 'Red, white, blue with white star', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('colombia', 'Colombia 🇨🇴', 'Yellow, blue, red tricolor', '45 90% 52%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('costa_rica', 'Costa Rica 🇨🇷', 'Blue, white, red, white, blue', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('cuba', 'Cuba 🇨🇺', 'Blue and white stripes with red triangle and star', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('dominica', 'Dominica 🇩🇲', 'Green with cross and sisserou parrot', '140 65% 28%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 18%'),
  makeCountryTheme('dominican_republic', 'Dominican Republic 🇩🇴', 'Blue, red quarters with white cross', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('ecuador', 'Ecuador 🇪🇨', 'Yellow, blue, red with coat of arms', '45 90% 52%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('el_salvador', 'El Salvador 🇸🇻', 'Blue, white, blue with coat of arms', '220 80% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('grenada', 'Grenada 🇬🇩', 'Red with yellow and green triangles and nutmeg', '0 80% 40%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('guatemala', 'Guatemala 🇬🇹', 'Blue and white with quetzal bird', '220 80% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('guyana', 'Guyana 🇬🇾', 'Green with red and black arrowheads on gold and white', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('haiti', 'Haiti 🇭🇹', 'Blue and red with coat of arms', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('honduras', 'Honduras 🇭🇳', 'Blue, white, blue with stars', '220 80% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('jamaica', 'Jamaica 🇯🇲', 'Black and green with gold X-cross', '0 0% 8%', '0 0% 95%', '45 85% 50%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('mexico', 'Mexico 🇲🇽', 'Green, white, red with eagle', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('nicaragua', 'Nicaragua 🇳🇮', 'Blue, white, blue with coat of arms', '220 80% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('panama', 'Panama 🇵🇦', 'White with red and blue quarters and stars', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('paraguay', 'Paraguay 🇵🇾', 'Red, white, blue with emblem', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('peru', 'Peru 🇵🇪', 'Red, white, red', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('saint_kitts_nevis', 'Saint Kitts & Nevis 🇰🇳', 'Green, black, yellow, red with stars', '140 65% 30%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '140 65% 20%'),
  makeCountryTheme('saint_lucia', 'Saint Lucia 🇱🇨', 'Teal with black and white triangle on gold', '175 65% 35%', '0 0% 5%', '0 0% 8%', '0 0% 98%', '175 65% 22%'),
  makeCountryTheme('saint_vincent', 'Saint Vincent 🇻🇨', 'Blue, yellow, green with diamonds', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('suriname', 'Suriname 🇸🇷', 'Green, white, red with gold star', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('trinidad_tobago', 'Trinidad & Tobago 🇹🇹', 'Red with black diagonal stripe edged in white', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 28%'),
  makeCountryTheme('uruguay', 'Uruguay 🇺🇾', 'White and blue stripes with golden sun', '220 80% 50%', '0 0% 5%', '0 0% 98%', '0 0% 5%', '220 80% 35%'),
  makeCountryTheme('venezuela', 'Venezuela 🇻🇪', 'Yellow, blue, red with stars and coat of arms', '45 90% 52%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('afghanistan', 'Afghanistan 🇦🇫', 'Black, red, green with emblem', '0 80% 40%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 0% 8%'),
  makeCountryTheme('armenia', 'Armenia 🇦🇲', 'Red, blue, orange tricolor', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('azerbaijan', 'Azerbaijan 🇦🇿', 'Blue, red, green with crescent and star', '200 75% 45%', '0 0% 5%', '0 80% 40%', '0 0% 98%', '200 75% 30%'),
  makeCountryTheme('bahrain', 'Bahrain 🇧🇭', 'Red with white serrated band', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('bangladesh', 'Bangladesh 🇧🇩', 'Green with red disc', '140 65% 28%', '0 0% 98%', '0 80% 42%', '0 0% 98%', '140 65% 18%'),
  makeCountryTheme('bhutan', 'Bhutan 🇧🇹', 'Orange and red split with white dragon', '25 90% 52%', '0 0% 5%', '0 80% 40%', '0 0% 98%', '25 90% 35%'),
  makeCountryTheme('brunei', 'Brunei 🇧🇳', 'Yellow with white and black diagonal stripes', '45 85% 50%', '0 0% 5%', '0 0% 8%', '0 0% 95%', '45 85% 35%'),
  makeCountryTheme('cambodia', 'Cambodia 🇰🇭', 'Red with blue stripes and Angkor Wat', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('china', 'China 🇨🇳', 'Red with yellow stars', '0 80% 38%', '0 0% 98%', '45 90% 55%', '0 0% 5%', '0 80% 25%'),
  makeCountryTheme('cyprus', 'Cyprus 🇨🇾', 'White with copper island and olive branches', '0 0% 98%', '0 0% 5%', '25 85% 52%', '0 0% 5%', '25 85% 35%'),
  makeCountryTheme('georgia_country', 'Georgia 🇬🇪', 'White with red cross and four smaller crosses', '0 0% 98%', '0 0% 5%', '0 80% 40%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('india', 'India 🇮🇳', 'Saffron, white, green with blue Ashoka Chakra', '25 90% 55%', '0 0% 5%', '140 65% 30%', '0 0% 98%', '25 90% 38%'),
  makeCountryTheme('indonesia', 'Indonesia 🇮🇩', 'Red and white bicolor', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('iran', 'Iran 🇮🇷', 'Green, white, red with emblem', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('iraq', 'Iraq 🇮🇶', 'Red, white, black with green text', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 28%'),
  makeCountryTheme('israel', 'Israel 🇮🇱', 'White and blue with Star of David', '220 80% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('jordan', 'Jordan 🇯🇴', 'Black, white, green with red triangle and star', '0 0% 8%', '0 0% 95%', '0 80% 40%', '0 0% 98%', '0 0% 5%'),
  makeCountryTheme('kazakhstan', 'Kazakhstan 🇰🇿', 'Light blue with golden sun and eagle', '200 70% 52%', '0 0% 5%', '45 85% 50%', '0 0% 5%', '200 70% 35%'),
  makeCountryTheme('kuwait', 'Kuwait 🇰🇼', 'Green, white, red with black trapezoid', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '0 0% 8%'),
  makeCountryTheme('kyrgyzstan', 'Kyrgyzstan 🇰🇬', 'Red with yellow sun and tunduk', '0 80% 40%', '0 0% 98%', '45 85% 50%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('laos', 'Laos 🇱🇦', 'Red, blue, red with white circle', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('lebanon', 'Lebanon 🇱🇧', 'Red, white, red with green cedar tree', '0 80% 40%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('malaysia', 'Malaysia 🇲🇾', 'Red and white stripes with blue canton, crescent and star', '0 80% 40%', '0 0% 98%', '220 80% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('maldives', 'Maldives 🇲🇻', 'Green with red border and white crescent', '140 65% 28%', '0 0% 98%', '0 80% 42%', '0 0% 98%', '140 65% 18%'),
  makeCountryTheme('mongolia', 'Mongolia 🇲🇳', 'Red, blue, red with Soyombo symbol', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('myanmar', 'Myanmar 🇲🇲', 'Yellow, green, red with white star', '45 90% 52%', '0 0% 5%', '140 65% 30%', '0 0% 98%', '45 90% 35%'),
  makeCountryTheme('nepal', 'Nepal 🇳🇵', 'Red with blue border double pennant', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('north_korea', 'North Korea 🇰🇵', 'Blue, white, red with white star circle', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('oman', 'Oman 🇴🇲', 'Red, white, green with khanjar emblem', '0 80% 38%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 80% 25%'),
  makeCountryTheme('pakistan', 'Pakistan 🇵🇰', 'Green with white crescent and star, white stripe', '140 65% 28%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '140 65% 18%'),
  makeCountryTheme('palestine', 'Palestine 🇵🇸', 'Black, white, green with red triangle', '0 0% 8%', '0 0% 95%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('philippines', 'Philippines 🇵🇭', 'Blue and red with white triangle and sun', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('qatar', 'Qatar 🇶🇦', 'Maroon with white serrated band', '345 70% 35%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '345 70% 22%'),
  makeCountryTheme('saudi_arabia', 'Saudi Arabia 🇸🇦', 'Green with white Arabic text and sword', '140 65% 25%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '140 65% 16%'),
  makeCountryTheme('singapore', 'Singapore 🇸🇬', 'Red and white with crescent and stars', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('south_korea', 'South Korea 🇰🇷', 'White with Taegukgi yin-yang and trigrams', '0 0% 98%', '0 0% 5%', '0 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('sri_lanka', 'Sri Lanka 🇱🇰', 'Dark red with gold lion and green/orange stripes', '345 70% 32%', '0 0% 98%', '45 85% 52%', '0 0% 5%', '345 70% 20%'),
  makeCountryTheme('syria', 'Syria 🇸🇾', 'Red, white, black with green stars', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 28%'),
  makeCountryTheme('taiwan', 'Taiwan 🇹🇼', 'Red with blue canton, white sun', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('tajikistan', 'Tajikistan 🇹🇯', 'Red, white, green with gold crown and stars', '0 80% 40%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('thailand', 'Thailand 🇹🇭', 'Red, white, blue, white, red bands', '0 80% 40%', '0 0% 98%', '220 80% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('timor_leste', 'Timor-Leste 🇹🇱', 'Red with black triangle and yellow triangle, white star', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 28%'),
  makeCountryTheme('turkey', 'Turkey 🇹🇷', 'Red with white crescent and star', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('turkmenistan', 'Turkmenistan 🇹🇲', 'Green with red stripe and carpet patterns', '140 65% 28%', '0 0% 98%', '0 80% 42%', '0 0% 98%', '140 65% 18%'),
  makeCountryTheme('uae', 'UAE 🇦🇪', 'Green, white, black with red stripe', '140 65% 28%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '0 0% 8%'),
  makeCountryTheme('uzbekistan', 'Uzbekistan 🇺🇿', 'Blue, white, green with crescent and stars', '200 70% 45%', '0 0% 5%', '140 65% 35%', '0 0% 98%', '200 70% 30%'),
  makeCountryTheme('vietnam', 'Vietnam 🇻🇳', 'Red with yellow star', '0 80% 38%', '0 0% 98%', '45 90% 55%', '0 0% 5%', '0 80% 25%'),
  makeCountryTheme('yemen', 'Yemen 🇾🇪', 'Red, white, black tricolor', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 28%'),
  makeCountryTheme('albania', 'Albania 🇦🇱', 'Red with black double-headed eagle', '0 80% 38%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 25%'),
  makeCountryTheme('andorra', 'Andorra 🇦🇩', 'Blue, yellow, red vertical with coat of arms', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('austria', 'Austria 🇦🇹', 'Red, white, red horizontal', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('belarus', 'Belarus 🇧🇾', 'Red and green with ornamental white-red stripe', '0 80% 40%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('belgium', 'Belgium 🇧🇪', 'Black, yellow, red vertical', '0 0% 8%', '0 0% 95%', '45 90% 52%', '0 0% 5%', '0 0% 5%'),
  makeCountryTheme('bosnia_herzegovina', 'Bosnia & Herzegovina 🇧🇦', 'Blue with yellow triangle and white stars', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('bulgaria', 'Bulgaria 🇧🇬', 'White, green, red horizontal', '0 0% 98%', '0 0% 5%', '140 65% 35%', '0 0% 98%', '140 65% 22%'),
  makeCountryTheme('croatia', 'Croatia 🇭🇷', 'Red, white, blue with coat of arms', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('czech_republic', 'Czech Republic 🇨🇿', 'Red, white with blue triangle', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('denmark', 'Denmark 🇩🇰', 'Red with white Scandinavian cross', '0 80% 38%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 25%'),
  makeCountryTheme('estonia', 'Estonia 🇪🇪', 'Blue, black, white horizontal', '220 80% 42%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '220 80% 28%'),
  makeCountryTheme('finland', 'Finland 🇫🇮', 'White with blue Scandinavian cross', '0 0% 98%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('greece', 'Greece 🇬🇷', 'Blue and white stripes with blue canton and white cross', '220 80% 42%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('hungary', 'Hungary 🇭🇺', 'Red, white, green horizontal', '0 80% 40%', '0 0% 98%', '140 65% 35%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('iceland', 'Iceland 🇮🇸', 'Blue with red and white cross', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('ireland', 'Ireland 🇮🇪', 'Green, white, orange vertical', '140 65% 30%', '0 0% 98%', '25 90% 55%', '0 0% 5%', '140 65% 20%'),
  makeCountryTheme('italy', 'Italy 🇮🇹', 'Green, white, red vertical', '140 65% 30%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('kosovo', 'Kosovo 🇽🇰', 'Blue with yellow map and stars', '220 80% 42%', '0 0% 98%', '45 85% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('latvia', 'Latvia 🇱🇻', 'Dark red, white, dark red horizontal', '345 70% 32%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '345 70% 20%'),
  makeCountryTheme('liechtenstein', 'Liechtenstein 🇱🇮', 'Blue and red with golden crown', '220 80% 42%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('lithuania', 'Lithuania 🇱🇹', 'Yellow, green, red horizontal', '45 90% 52%', '0 0% 5%', '140 65% 30%', '0 0% 98%', '140 65% 20%'),
  makeCountryTheme('luxembourg', 'Luxembourg 🇱🇺', 'Red, white, light blue horizontal', '0 80% 40%', '0 0% 98%', '200 75% 55%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('malta', 'Malta 🇲🇹', 'White and red with George Cross', '0 0% 98%', '0 0% 5%', '0 80% 40%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('moldova', 'Moldova 🇲🇩', 'Blue, yellow, red with coat of arms', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('monaco', 'Monaco 🇲🇨', 'Red and white horizontal', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('montenegro', 'Montenegro 🇲🇪', 'Red with golden border and eagle', '0 80% 40%', '0 0% 98%', '45 85% 50%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('netherlands', 'Netherlands 🇳🇱', 'Red, white, blue horizontal', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('north_macedonia', 'North Macedonia 🇲🇰', 'Red with golden sun', '0 80% 40%', '0 0% 98%', '45 85% 52%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('norway', 'Norway 🇳🇴', 'Red with blue and white Scandinavian cross', '0 80% 38%', '0 0% 98%', '220 80% 28%', '0 0% 98%', '0 80% 25%'),
  makeCountryTheme('poland', 'Poland 🇵🇱', 'White and red horizontal', '0 0% 98%', '0 0% 5%', '0 80% 40%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('portugal', 'Portugal 🇵🇹', 'Green and red with coat of arms', '140 65% 28%', '0 0% 98%', '0 80% 40%', '0 0% 98%', '140 65% 18%'),
  makeCountryTheme('romania', 'Romania 🇷🇴', 'Blue, yellow, red vertical', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('russia', 'Russia 🇷🇺', 'White, blue, red horizontal', '0 0% 98%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('san_marino', 'San Marino 🇸🇲', 'White and light blue with coat of arms', '0 0% 98%', '0 0% 5%', '200 70% 52%', '0 0% 5%', '200 70% 35%'),
  makeCountryTheme('serbia', 'Serbia 🇷🇸', 'Red, blue, white with coat of arms', '0 80% 40%', '0 0% 98%', '220 80% 42%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('slovakia', 'Slovakia 🇸🇰', 'White, blue, red with coat of arms', '0 0% 98%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('slovenia', 'Slovenia 🇸🇮', 'White, blue, red with coat of arms', '0 0% 98%', '0 0% 5%', '220 80% 42%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('spain', 'Spain 🇪🇸', 'Red and yellow with coat of arms', '0 80% 38%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '0 80% 25%'),
  makeCountryTheme('sweden', 'Sweden 🇸🇪', 'Blue with yellow Scandinavian cross', '220 80% 35%', '0 0% 98%', '45 90% 55%', '0 0% 5%', '220 80% 22%'),
  makeCountryTheme('switzerland', 'Switzerland 🇨🇭', 'Red with white cross', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('ukraine', 'Ukraine 🇺🇦', 'Blue and yellow horizontal', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('united_kingdom', 'United Kingdom 🇬🇧', 'Union Jack — blue, red, white', '220 80% 30%', '0 0% 98%', '0 80% 45%', '0 0% 98%', '220 80% 20%'),
  makeCountryTheme('vatican', 'Vatican City 🇻🇦', 'Yellow and white with crossed keys and tiara', '45 85% 50%', '0 0% 5%', '0 0% 98%', '0 0% 5%', '45 85% 35%'),
  makeCountryTheme('australia', 'Australia 🇦🇺', 'Blue with Union Jack, stars and Commonwealth Star', '220 80% 30%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '220 80% 20%'),
  makeCountryTheme('fiji', 'Fiji 🇫🇯', 'Light blue with Union Jack and shield', '200 70% 50%', '0 0% 5%', '220 80% 30%', '0 0% 98%', '200 70% 35%'),
  makeCountryTheme('kiribati', 'Kiribati 🇰🇮', 'Red with rising sun and frigatebird over blue waves', '0 80% 40%', '0 0% 98%', '220 80% 50%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('marshall_islands', 'Marshall Islands 🇲🇭', 'Dark blue with diagonal stripes and star', '220 80% 30%', '0 0% 98%', '0 80% 42%', '0 0% 98%', '220 80% 20%'),
  makeCountryTheme('micronesia', 'Micronesia 🇫🇲', 'Light blue with four white stars', '200 75% 52%', '0 0% 5%', '0 0% 98%', '0 0% 5%', '200 75% 35%'),
  makeCountryTheme('nauru', 'Nauru 🇳🇷', 'Blue with yellow stripe and white star', '220 80% 42%', '0 0% 98%', '45 90% 52%', '0 0% 5%', '220 80% 28%'),
  makeCountryTheme('new_zealand', 'New Zealand 🇳🇿', 'Blue with Union Jack and Southern Cross stars', '220 80% 30%', '0 0% 98%', '0 80% 45%', '0 0% 98%', '220 80% 20%'),
  makeCountryTheme('palau', 'Palau 🇵🇼', 'Light blue with yellow disc', '200 70% 55%', '0 0% 5%', '45 85% 52%', '0 0% 5%', '200 70% 38%'),
  makeCountryTheme('papua_new_guinea', 'Papua New Guinea 🇵🇬', 'Red and black diagonal with golden bird of paradise and stars', '0 80% 40%', '0 0% 98%', '0 0% 8%', '0 0% 95%', '0 80% 28%'),
  makeCountryTheme('samoa', 'Samoa 🇼🇸', 'Red with blue canton and white Southern Cross', '0 80% 40%', '0 0% 98%', '220 80% 30%', '0 0% 98%', '0 80% 28%'),
  makeCountryTheme('solomon_islands', 'Solomon Islands 🇸🇧', 'Blue and green diagonal with yellow stars', '220 80% 42%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '220 80% 28%'),
  makeCountryTheme('tonga', 'Tonga 🇹🇴', 'Red with white canton and red cross', '0 80% 40%', '0 0% 98%', '0 0% 98%', '0 0% 5%', '0 80% 28%'),
  makeCountryTheme('tuvalu', 'Tuvalu 🇹🇻', 'Light blue with Union Jack and yellow stars', '200 70% 52%', '0 0% 5%', '220 80% 30%', '0 0% 98%', '200 70% 35%'),
  makeCountryTheme('vanuatu', 'Vanuatu 🇻🇺', 'Red, green, black with yellow Y and boar tusk', '0 80% 40%', '0 0% 98%', '140 65% 30%', '0 0% 98%', '0 80% 28%'),

];
